<script setup lang="ts">
import { getAreaOptionLabel, getAreaSingular as areaSingularForActivity } from "@/utils/areaNaming";
import { getBookingOptionQuantities, getBookingOptionQuantity } from "@/utils/bookingOptionQuantities";
import BookingExtras from "./steps/BookingExtras.vue";
import DialogReceipt from "@/components/dialogReceipt.vue";
import { usePaymentStore } from "@/components/payment/usePaymentStore";
import { useBooking as useBookingStore } from "@/store/useBooking";
import { useDiscountCode } from "@/store/useDiscountCode";
import { useGlobalErrorStore } from "@/store/useGlobalError";
import { useTodPricing } from "@/store/useTodPricing";
import { useUserModeChange } from "@/store/useUserModeChange";
import { allowedDatesRecurring, doesUserHaveCoupons, formatDurationLabel, formatDurationValue, getMembershipDiscounts, mapSelectionToMinutes, useInactivityTimer } from "@/utils/BookingStepperUtil";
import { formatPrice } from "@/utils/Formatting";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { requiredValidator } from "@validators";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useTheme } from "vuetify";
import { VForm } from "vuetify/components/VForm";
import StepperFooter from "./StepperFooter.vue";
import BookingOverview from "./steps/BookingOverview.vue";
import NumberInPartyStep from "./steps/NumberInPartyStep.vue";
import type { BookingEmit, BookingProps } from "./types";
import { useBooking } from "./useBooking";

// props
const props = defineProps<BookingProps>();

// Emit
const emit = defineEmits<BookingEmit>();

const vuetifyTheme = useTheme();

// Store
const userStore = useUserStore();
const globalErrorStore = useGlobalErrorStore();
const bookingStore = useBookingStore();
const paymentStore = usePaymentStore();
const discount = useDiscountCode();
const todStore = useTodPricing();
const userMode = useUserModeChange();
const { isUserMode } = storeToRefs(userMode);
const globalSetupStore = useGlobalSetupStore();
const { globalSetup } = storeToRefs(globalSetupStore);

const { handleUpdateErrorMessage } = globalErrorStore;
const { getItemTOD } = todStore;

const editingBooking = () => {
  localStorage.removeItem('onpayment')
  emit("isEditing",true)
}

// Get Discounts using Owner of the Booking
const userMembershipDiscounts = ref<any>({})

const getFormBookingOwner = async(ev:any) => {
  form.value.owner = ev
  userMembershipDiscounts.value = await getMembershipDiscounts(form.value?.owner ?? props?.booking?.owner)
}

const { globalErrorMessage, isBookingWarningDialogVisible, errorDateList } = storeToRefs(globalErrorStore);
const { docketId, docketLineId, isPendingBooking } = storeToRefs(paymentStore);
const { recurringBookingId, activityList } = storeToRefs(bookingStore);
const { discountCode, discountCodeData } = storeToRefs(discount);
const { getUserCreditCards } = userStore;
const { userCreditCards, currentLocationDetails, currentLocation } = storeToRefs(userStore);
// composable
const booking = useBooking();

const {
  paymentOptions,
  recurringWeeks,
  getAreaSingular,
  alternativeAreaList,
  getDuration,
  reserveSlot,
  removeExistingPendingBooking,
  updateBooking,
  refetchBooking,
  handleRecurringBooking,
  getDayIdFromDate,
  getAvailableHoursForDay,
} = booking;

// Router
const router = useRouter();

// Refs
const dateTimeForRef = ref<any>("");
const memberVisitorsFormRef = ref<any>("");
const numberInPartyFormRef = ref<any>(null);
const agreementFormRef = ref<any>("");
const refRecurringVForm = ref<any>("");
const todAdjustment = ref<number>(0);
const todSource = ref<string>("");
const docketDiscount = ref<number>(0);
const discountApplied = ref<string>("None");
const lineDiscountTotal = ref<number>(0);
const dueAmount = ref<number>(0);
const bookingTotal = ref<number>(0);
const showTermsConditionsDialog = ref<boolean>(false);
const availableTimes = ref<any>([]);

// Data
let durationUserChanged = false;
const isBookingViewOnly = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const isSubmitting = ref<boolean>(false);
const isYetToPayDues = ref<boolean>(false);

const form = ref<any>({
  id: "",
  title: "",
  date: undefined,
  activity: "",
  guests: [],
  area: undefined,
  time: undefined,
  notes: "",
  members: [],
  agreement: false,
  notifyMe: false,
  owner: "",
  isRecurring: false,
  selectedOptions: [],
  optionQuantities: {},
  visitorEmail: "",
  numberInParty: 1,
});

const recurring = ref<any>({
  frequency: 1,
  interval: "Weeks",
  days: [],
  endDate: "",
});

const maxPlayers = ref<number>(0);
const minPlayers = ref<number>(0);
const memberSearchQuery = ref("");
const filteredMemberList = computed(() => {
  const allMembers = props?.memberList ?? [];
  const selected = allMembers.filter((m: any) => form.value.members?.includes(m.id));
  if (memberSearchQuery.value.length < 3) return selected;
  return allMembers;
});
const role = ref<string>("Member");
const bookingCreatedDate = ref<any>("");
const currentStep = ref<number>(0);
const pendingBookingId = ref<any>("");
const showPopup = ref<boolean>(false);
const actualEndTime = ref<any>("");
const isCancelDialogVisible = ref<boolean>(false);
const selectedDurationSlot = ref<number>(0);
const isLeftHandedWarningDialogVisible = ref<boolean>(false);
const isLeftHandedBookingErrorVerified = ref<boolean>(false);

// computed
const canEditDateAndTimePage = () => {
  if(props?.permission && Array.isArray(props?.permission) && props?.permission.includes("Create")){
   return true
  }
  return false
}

const bookingSteps = computed(() => [
  ...(canEditDateAndTimePage() ? [
  {
    id: 1,
    title: "Date & Time",
    subtitle: "",
    icon: "tabler-calendar",
    size: 30,
    isValid: true,
  }] : []),
  {
    id: 2,
    title: selectedActivity?.value?.maxPlayers > 1 ? "Options" : "Extras",
    subtitle: "",
    icon: selectedActivity?.value?.maxPlayers > 1 ? "tabler-users" : "mdi-page-next-outline",
    size: 30,
    isValid: true,
  },
  ...(currentLocationDetails.value?.numberInPartyField
    ? [
        {
          id: 6,
          title: "Party Size",
          subtitle: "",
          icon: "tabler-users-group",
          size: 30,
          isValid: true,
        },
      ]
    : []),
  ...(form.value.isRecurring
    ? [
        {
          id: 3,
          title: "Recurring Booking",
          subtitle: "",
          icon: "tabler-repeat",
          size: 30,
          isValid: true,
        },
      ]
    : []),
  ...(!isInProgressBooking.value
    ? [
        {
          id: 4,
          title: "Agreement",
          subtitle: "",
          icon: "mdi-file-document-edit",
          size: 30,
          isValid: true,
        },
      ]
    : []),
  {
    id: 5,
    title: "Payment",
    icon: "tabler-cards",
    size: 30,
    isValid: false,
  },
]);

const productList = computed(() => {
  return (selectedActivity.value?.expand?.options || []).filter(
    (option: any) => option.location === currentLocation.value && !option.inactive
  );
});

const haveEditAccess = computed(() => {
  return props?.permission && props?.permission.includes("Edit");
});

const haveCreateAccess = computed(() => {
  return props?.permission && props?.permission.includes("Create");
});

const isEditBooking = computed(() => props?.booking?.id);

const bookingStatus = computed(() => props?.booking?.status);

// Mirrors how the booking is mapped onto the form, so an untouched form compares equal.
const savedBookingValues = computed(() => {
  const booking: any = props?.booking;
  if (!booking?.id) return null;

  const isStaff =
    (role.value === "Admin" || haveEditAccess.value || haveCreateAccess.value) &&
    !isUserMode.value;
  const isInProgress = booking.status === "inprogress";

  return {
    activity: booking.activity,
    area: booking.area,
    date: booking.startDate ? moment(booking.startDate).format("YYYY-MM-DD") : null,
    time: booking.startDate ? moment(booking.startDate).format("HH:mm") : null,
    notes: booking.notes,
    members: booking.users ?? (isStaff ? [] : [props.userId]),
    owner: booking.owner || null,
    guests: booking.visitors
      ? booking.visitors.split(",").map((item: string) => item.trim())
      : null,
    durationMinutes: booking.duration,
    selectedOptions: booking.selectedOptions || [],
    optionQuantities: getBookingOptionQuantities(booking.selectedOptions, booking.optionQuantities),
    visitorEmail: booking.visitorEmail || "",
    numberInParty: booking.numberInParty || 1,
    actualStartTime:
      isInProgress && booking.actualStartTime
        ? moment(booking.actualStartTime).format("DD-MM-YYYY hh:mm a")
        : null,
    actualDuration: isInProgress ? booking.actualDuration || null : null,
    isRecurring: !isEmpty(booking.recurring),
    recurring: !isEmpty(booking.recurring)
      ? {
          endDate: moment(booking.recurring.endDate).format("YYYY-MM-DD"),
          frequency: booking.recurring.frequency,
          interval: booking.recurring.interval,
          days: recurringWeeks.value
            ?.filter((week: any) =>
              booking.recurring.days
                ?.split(", ")
                ?.map((day: string) => day.trim())
                ?.includes(week.value)
            )
            .map((week: any) => week.id),
        }
      : null,
  };
});

const currentBookingValues = computed(() => ({
  activity: form.value.activity,
  area: form.value.area,
  date: form.value.date ? moment(form.value.date).format("YYYY-MM-DD") : null,
  time: form.value.time,
  notes: form.value.notes,
  members: form.value.members,
  owner: form.value.owner || null,
  guests: form.value.guests,
  durationMinutes: form.value.duration
    ? mapSelectionToMinutes(String(form.value.duration)?.replace(":", "."))
    : null,
  selectedOptions: form.value.selectedOptions || [],
  optionQuantities: getBookingOptionQuantities(form.value.selectedOptions, form.value.optionQuantities),
  visitorEmail: form.value.visitorEmail || "",
  numberInParty: form.value.numberInParty || 1,
  actualStartTime: form.value.actualStartTime ?? null,
  actualDuration: form.value.actualDuration ?? null,
  isRecurring: !!form.value.isRecurring,
  recurring: form.value.isRecurring
    ? {
        endDate: moment(recurring.value?.endDate).format("YYYY-MM-DD"),
        frequency: recurring.value?.frequency,
        interval: recurring.value?.interval,
        days: [...(recurring.value?.days ?? [])],
      }
    : null,
}));

const isBookingDirty = computed(() => {
  if (!savedBookingValues.value) return true;
  return !isEqual(savedBookingValues.value, currentBookingValues.value);
});

const selectedActivity = computed(() =>
  props?.activityList?.find((el) => el.id === form.value.activity)
);

// Example booking space value, can be 15, 30, 45, or 60
const getBookingSpacing = computed(
  () => selectedActivity.value?.sessionDuration || 60
);

const bookingSlot = computed(() => {
  return {
    slotQty: selectedDurationSlot.value,
    sessionPrice: selectedActivity.value.sessionFullPrice,
  };
});

const getAgreementMessage = computed(
  () => selectedActivity.value?.bookingAgreement
);

const getProductDiscount = computed(() => {
  let docketProductDiscount = 0;
  if(docketDiscounts.value && docketDiscounts.value?.id){
    const {discManProducts,discMemProducts,discountProducts, discCodeProducts} = docketDiscounts.value
    docketProductDiscount = Math.max(discManProducts,discMemProducts,discountProducts,discCodeProducts)
  }

  return Math.max(
    docketProductDiscount,
    discountCodeData.value?.discountProduct || 0,
    userMembershipDiscounts.value?.discountProduct || 0
  );
});

const discountSources = computed(() => {
  const sources = new Set();

  if (docketDiscount.value > 0) {
    if ((userMembershipDiscounts.value?.discountFurtherBookings && userMembershipDiscounts.value?.discountFurtherBookings > 0) ||
      (
      (props?.membership?.discountFurtherBookings || 0) >=
      (discountCodeData.value?.discountBookings || 0))
    ) {
      sources.add("Membership");
    } else {
      sources.add("DiscountCode");
    }
  }

  if (getProductDiscount.value > 0) {
    if (
      (userMembershipDiscounts.value?.discountProduct && userMembershipDiscounts.value?.discountProduct > 0) ||
      (
      (props?.membership?.discountProduct || 0) >=
      (discountCodeData.value?.discountProduct || 0))
    ) {
      sources.add("Membership");
    } else {
      sources.add("DiscountCode");
    }
  }

  return Array.from(sources);
});

const isInProgressBooking = computed(() => {
  return props?.booking?.status === "inprogress";
});

const getDurationList = computed(() => {
  if (!form.value.activity) return [];

  let gap = selectedActivity.value?.bookingGap || 0
  let sessionLimit = selectedActivity.value?.sessionLimit || 1

  // Cap sessionLimit by how many slots remain from the selected time.
  // Only apply when availableTimes is loaded — timeList may only contain the booking's
  // own slot before availability is fetched, which would incorrectly cap to 1.
  if (form.value.time && availableTimes.value?.length > 0) {
    const selectedIndex = timeList.value.findIndex((t: any) => t.value === form.value.time);
    const isAdmin = (role.value === "Admin" || haveEditAccess.value || haveCreateAccess.value) && !isUserMode.value;
    if (selectedIndex !== -1 && !isAdmin) {
      const slotsRemaining = timeList.value.length - selectedIndex;
      sessionLimit = Math.min(sessionLimit, slotsRemaining);
    }
  }

  const durationList = [];

  // Calculate total slots
  const numberOfSlots = sessionLimit;

  for (let i = 0; i < numberOfSlots; i++) {
    const durationMinutes = (i + 1) * getBookingSpacing.value;
    durationList.push({
      value: formatDurationValue(durationMinutes, getBookingSpacing.value % 60 === 0),
      text: formatDurationLabel(durationMinutes - gap),
    });
  }
  return durationList;
});

// configure disable condition
// Any of these one required for edit access
// Role other than Admin
const isFieldsDisabled = computed(() => {
  if (form.value.id) {
    if (bookingStatus.value !== "futureBooking") {
      return true;
    }
    return false;
  }
  return false;
});

const areaDurationDisable = computed(() => {
  if (form.value.id) {
    if (bookingStatus.value !== "futureBooking") {
      if (haveEditAccess.value) {
        return false;
      }
      return true;
    }
    return false;
  }
  return false;
});

const canHire = (id:string) => {
  let canHireItem = false
  timeList.value.forEach((time:any) => {
    if(time.value == id && time.isNormalHour){
      canHireItem = true
    }
  })
  return canHireItem
}

const isAfterHours = async(time: string) => {
  if (!form.value.date || !selectedActivity.value) return false;
  const response = await pb.send('/bbapi/times/getTimeData', {
    method: 'POST',
    body: {
      activity: selectedActivity.value?.id,
      membership: props?.membership || [],
      date: form.value.date,
      time: time,
    },
    requestKey: null,
  });
  return !response.allUsers
};

const afterHours = ref(false)
const userHasCoupons = ref(false)

watch(
  () => form.value?.time,
  async (time) => {
    if (time) {
      afterHours.value = await isAfterHours(time)
      const validDurations = getDurationList.value.map((d: any) => d.value);
      if (form.value.duration && !validDurations.includes(form.value.duration)) {
        form.value.duration = getDurationList.value[0]?.value ?? null;
      }
    } else {
      afterHours.value = false
    }

    userHasCoupons.value = await doesUserHaveCoupons(form.value.owner, afterHours.value, form.value.activity)
  },
  { immediate: true }
)

const timeList = computed(() => {
  if (!form.value.date) return [];

  const interval = selectedActivity.value?.bookingSpacing || getBookingSpacing.value;

  const isToday = moment(form.value?.date).isSame(moment(), "day");

  const filterPastTimes = (hour: any, min: any) => {
    if (!isToday) return true;

    const currentTime = moment();
    const slotEnd = moment().hour(hour).minute(min).add(interval, "minutes");

    return slotEnd.isAfter(currentTime, "minute");
  };

  const formatTimeTo12Hour = (hour: any, min: any) => {
    return moment(`${hour}:${min}`, "HH:mm").format("hh:mm A");
  };

  let startMins = 0
  if (getAreaList?.value) {
    const area = getAreaList?.value.filter(a => a.id === form.value?.area);
    startMins = area[0]?.offSetMinutes || 0;
  }

    interface DynamicTimeItem {
      times: string;
      allUsers?: boolean;
    }

    const dynamicTimeList: DynamicTimeItem[] = [];

    const addTime = (time: string, allUsers: boolean) => {
      const foundIndex = dynamicTimeList.findIndex((dynamicTime: any) => dynamicTime.times === time);

      if (foundIndex !== -1) {
        // Optionally update the existing entry, e.g. merge allUsers
        dynamicTimeList[foundIndex].allUsers = dynamicTimeList[foundIndex].allUsers || allUsers;
      } else {
        dynamicTimeList.push({times: time, allUsers});
      }
    }

    availableTimes.value?.forEach((timeSlot: any) => {
      const {startHour, startMin: slotStartMin, finishHour, finishMin: slotFinishMin, allUsers} = timeSlot;
      const slotStartMinute = slotStartMin || 0;
      const slotFinishMinute = slotFinishMin || 0;
      const finishTotalMins = finishHour * 60 + slotFinishMinute;

      if ((startHour === 0 && finishHour === 0)) {
        // Full-day availability
        for (let hour = 0; hour < 24; hour++) {
          for (let min = startMins; min < 60; min += interval) {
            if (isToday && !filterPastTimes(hour, min)) continue;

            const formattedHour = hour.toString().padStart(2, "0");
            const formattedMin = min.toString().padStart(2, "0");
            addTime(`${formattedHour}:${formattedMin}`, allUsers);
          }
        }
      } else if (startHour === 0) {
        // Start at midnight and go until `finishHour`
        for (let hour = 0; hour < finishHour; hour++) {
          for (let min = startMins; min < 60; min += interval) {
            if (hour * 60 + min + interval > finishTotalMins) continue;
            if (isToday && !filterPastTimes(hour, min)) continue;

            const formattedHour = hour.toString().padStart(2, "0");
            const formattedMin = min.toString().padStart(2, "0");
            addTime(`${formattedHour}:${formattedMin}`, allUsers);
          }
        }
      } else if (finishHour === 0) {
        // Start at `startHour` and go until midnight
        for (let hour = startHour; hour < 24; hour++) {
          for (let min = startMins; min < 60; min += interval) {
            if (hour === startHour && min < slotStartMinute) continue;
            if (isToday && !filterPastTimes(hour, min)) continue;

            const formattedHour = hour.toString().padStart(2, "0");
            const formattedMin = min.toString().padStart(2, "0");

            addTime(`${formattedHour}:${formattedMin}`, allUsers);
          }
        }
      } else {
        // Normal range
        for (let hour = startHour; hour < finishHour; hour++) {
          for (let min = startMins; min < 60; min += interval) {
            if (hour === startHour && min < slotStartMinute) continue;
            if (hour * 60 + min + interval > finishTotalMins) continue;
            if (isToday && !filterPastTimes(hour, min)) continue;

            const formattedHour = hour.toString().padStart(2, "0");
            const formattedMin = min.toString().padStart(2, "0");
            addTime(`${formattedHour}:${formattedMin}`, allUsers);
          }
        }
      }
    });

    // For existing bookings, ensure the current booking time is always in the list
    // even if it's been filtered out (past time or membership-restricted slot)
    if (form.value.id && form.value.time && !dynamicTimeList.some((t: any) => t.times === form.value.time)) {
      dynamicTimeList.push({ times: form.value.time, allUsers: true });
    }

    return dynamicTimeList
      .sort((a:any,b:any) => a.times == b.times ? 0 : a.times < b.times ? -1 : 1)
      .map((time: any) => ({
        label: formatTimeTo12Hour(time.times.split(":")[0], time.times.split(":")[1]),
        value: time.times,
        color: vuetifyTheme.name.value,
        isNormalHour: role.value === "Admin" || haveEditAccess.value || haveCreateAccess.value ? true : time?.allUsers,
        isAfterHours: afterHours.value
      }));
});

const isInCurrentTimeSlot = computed(() => {
  if (haveCreateAccess.value) {
    return false;
  }

  const currentTime = moment();
  const bookingStart = moment(
    `${form.value?.date} ${form.value?.time}`,
    "YYYY-MM-DD HH:mm"
  );

  // Determine the duration based on activity type
  const activity = activityList.value?.find(
    (activity) => activity.id === form.value?.activity
  );

  const bookingDuration = activity?.bookingSpacing;
  const bookingEnd = bookingStart.clone().add(bookingDuration, "minutes");

  return currentTime.isBetween(bookingStart, bookingEnd);
});

const isOutHourWarningVisible = computed(() => {
  if (haveCreateAccess.value) {
    return false;
  }

  const dayName = form.value.date
    ? moment(form.value.date).format("dddd").toLowerCase()
    : "sunday";

  const filteredTimes = availableTimes.value?.filter((timeSlot: any) => {
    if (!timeSlot[dayName]) {
      return false;
    }

    // Exclude slots where start and finish times are both 0
    if (timeSlot.startHour === 0 && timeSlot.finishHour === 0) {
      return false;
    }

    return true;
  });


  if (!filteredTimes || filteredTimes.length === 0) {
    return true;
  }

  const bookingTime = (() => {
    const [hours, minutes] = form.value?.time?.split(":")?.map(Number);
    const totalMinutes = hours * 60 + minutes;
    return totalMinutes;
  })();

  const isOutOfRange = !filteredTimes.some((timeSlot: any) => {
    const startTime = timeSlot.startHour * 60 + timeSlot.startMin;
    const endTime = timeSlot.finishHour * 60 + timeSlot.finishMin;

    // Adjust for overnight time slots
    const isOvernight = timeSlot.startHour > timeSlot.finishHour;
    const isValid = isOvernight
      ? bookingTime >= startTime || bookingTime < endTime
      : bookingTime >= startTime && bookingTime < endTime;

    return isValid;
  });

  return isOutOfRange;
});

const getAreaList = computed(() => {
  if (form.value?.activity) {
    const isAdmin =
      (role.value === "Admin" || haveEditAccess.value || haveCreateAccess.value) &&
      !isUserMode.value;

    return props.areaList?.filter(
      (area: any) =>
        area.activities.includes(form.value?.activity) &&
        (!area.downForMaintenance || isAdmin)
    );
  }
  return [];
});

const selectedProducts = computed(() => {
  return form.value.selectedOptions?.map((optionId: any) => {
    const option = (selectedActivity.value?.expand?.options || []).find(
      (opt: any) => opt.id === optionId
    );

    if (option && option != undefined) {
      const docketDiscount: any = option.maxDiscount
        ? Math.min(getProductDiscount.value, option.maxDiscount)
        : getProductDiscount.value;

      let currentTodDiscount = 0;
      let todSource = "";
      
      // Get todAdjustment for time of booking
      if (form.value.time && form.value.date){
        const hour = form.value.time.split(":")[0];
        const min = form.value.time.split(":")[1];
        const dateTime = moment(form.value.date).hour(hour).minute(min);
        const todData = getItemTOD(option, dateTime);
        currentTodDiscount = todData.todAdjustment;
        todSource = todData.todSource;
      }

      // Apply TOD increase to price first (positive percentChange = price increase)
      const priceAfterTodIncrease = currentTodDiscount > 0
        ? (option.sellPrice || 0) * (1 + currentTodDiscount / 100)
        : (option.sellPrice || 0);

      const todDiscountValue = currentTodDiscount < 0 ? -currentTodDiscount : 0;
      const finalDiscount = docketDiscount > todDiscountValue ? docketDiscount : todDiscountValue;
      const discountAmount = priceAfterTodIncrease * (finalDiscount / 100);

      const discountApplied = docketDiscount > todDiscountValue ? "Membership" : todDiscountValue > 0 ? "TOD" : "None";

      return {
        id: option?.id,
        name: option?.name,
        docketDiscount: docketDiscount,
        todAdjustment: currentTodDiscount,
        todSource: todSource,
        lineDiscountTotal: discountAmount * getBookingOptionQuantity(form.value.optionQuantities, optionId),
        discountApplied: discountApplied,
        sellPrice: option?.sellPrice,
        itemPrice: parseFloat((priceAfterTodIncrease - discountAmount).toFixed(2)),
        qty: getBookingOptionQuantity(form.value.optionQuantities, optionId),
      };
    }
  }).filter((item: any) => item !== undefined) || [];
});

const addMinuteAmount = computed(() => {
  let costMins = 0;

  // Get Original Time
  let activity = props?.activityList?.filter(act => act.id === props?.booking?.activity)[0]

  let originalStartTime = moment(props?.booking?.startDate)
  let actualStartTime = moment(props?.booking?.actualStartTime)
  let OriginalMinutesLeft = originalStartTime.diff(actualStartTime,"minutes")

  if(OriginalMinutesLeft < 0){
    // They Started Late
    OriginalMinutesLeft = OriginalMinutesLeft * -1
  } else {
    // They Started Early
    OriginalMinutesLeft = 0;
  }

  // Calculate the limit for the largest duration, considering the bookingSpace
  const bookingSpace = activity?.bookingGap || 5; // Adjust this value based on your bookingSpace requirements
  let actualOriginalDuration = props?.booking?.duration - (bookingSpace + OriginalMinutesLeft)
  let extraMins = form?.value?.actualDuration - actualOriginalDuration;

  costMins = extraMins > 0 ? extraMins * activity?.extraPerMinFee : 0

  return costMins;
});

const isLeftHandWarning = computed(() => {
  // Check if required data is available
  if (!form.value?.area || !form.value?.members?.length) {
    return false;
  }
  // Find the area object based on the selected area ID
  const selectedArea = props?.areaList?.find(
    (area) => area.id === form.value.area
  );

  // If the selected area cannot accommodate left-handed members
  if (!selectedArea?.canLeftHanded) {
    // Check if any of the selected members is left-handed
    const isLeftHandedMemberSelected = props?.memberList?.some(
      (member) => form.value.members.includes(member.id) && member.leftHanded
    );
    // Return true if a left-handed member is selected
    return isLeftHandedMemberSelected;
  }
  // If the selected area can accommodate left-handed members, return false
  return false;
});

const durationExtendListMap = computed(() => {
  let durationTotal = mapSelectionToMinutes(
    String(form.value.duration)?.replace(":", ".")
  );

  let originalStartTime = moment(props?.booking?.startDate)
  let actualStartTime = moment(props?.booking?.actualStartTime)
  let OriginalMinutesLeft = originalStartTime.diff(actualStartTime,"minutes")

  const endLimit = moment(props?.booking?.startDate).add(
    durationTotal + OriginalMinutesLeft,
    "minutes"
  );

  return durationExtendList.value.map((t:any, index:number) => {
    let newTime = moment(props?.booking?.startDate).add(t,"minutes")
    let title = durationExtendList.value[0] == t ? t + ' mins (Original Time)' :  t + ' mins (+' + (t - durationExtendList.value[0]) + ' mins)'
    if(newTime.isAfter(endLimit)){
      let minsPast = newTime.diff(endLimit, "minutes")
      if(OriginalMinutesLeft < 0){
        const timeDiff = t - durationExtendList.value[0];
        title = `${t} mins (${timeDiff > 0 ? '+' : ''} ${timeDiff} mins (${minsPast} mins into next slot))`;
      } else {
        let addMins = props?.booking?.actualDuration
        const timeDiff = t - addMins;
        title = `${t} mins (${timeDiff > 0 ? '+' : ''}${timeDiff} mins (${minsPast} mins into next slot))`;
      }
    }
    return {
      'value':t,
      'title': title
    }
  })
})

const durationExtendList = computed(() => {
  const durationList = [] as any;

  // Convert duration to minutes and calculate the end limit
  let durationTotal = mapSelectionToMinutes(
    String(form.value.duration)?.replace(":", ".")
  );

  const endLimit = moment(props?.booking?.startDate).add(durationTotal, "minutes");

  // Get the actual start time and actual duration in minutes
  const startTime = moment(
    form.value?.actualStartTime,
    "DD-MM-YYYY hh:mm a",
    true
  );

  // Get Original Time
  let activity = props?.activityList?.filter(act => act.id === props?.booking?.activity)[0]

  let originalStartTime = moment(props?.booking?.startDate)
  let actualStartTime = moment(props?.booking?.actualStartTime)
  let OriginalMinutesLeft = originalStartTime.diff(actualStartTime,"minutes")

  if(OriginalMinutesLeft < 0){
    // They Started Late
    OriginalMinutesLeft = OriginalMinutesLeft * -1
  } else {
    // They Started Early
    OriginalMinutesLeft = 0;
  }

  // Calculate the limit for the largest duration, considering the bookingSpace
  const bookingSpace = activity?.bookingGap || 5; // Adjust this value based on your bookingSpace requirements

  let nextDuration =
    props?.booking?.actualDuration < props?.booking?.duration
      ? props?.booking?.actualDuration
      : props?.booking?.duration - bookingSpace;
  const maxDuration = endLimit.diff(startTime, "minutes") - bookingSpace - 1;

  // Generate duration list with increments of 5 minutes
  while (nextDuration <= maxDuration) {
    durationList.push(nextDuration);
    nextDuration += 5;
  }

  // If the final duration is within the maxDuration, add any remaining minutes
  if (durationList.length > 0 && nextDuration - 5 < maxDuration) {
    const remainingMinutes = maxDuration - (nextDuration - 5);
    if (remainingMinutes > 0) {
      durationList.push(
        durationList[durationList.length - 1] + remainingMinutes
      );
    }
  }

  let end = durationList[durationList.length - 1] || (props?.booking?.duration - bookingSpace - OriginalMinutesLeft)

  if(durationList.length === 0) {
    durationList.push(end)
  }

  if(props?.booking?.actualDuration && !isBookingAfter.value) {
    durationList.push(end + 15)
    durationList.push(end + 30)
  }
  return durationList;
});

const getBookingAmount = computed(() => {
  if (form.value?.activity && form.value?.area && form.value?.duration) {
    const durationTotal = mapSelectionToMinutes(
      String(form.value?.duration)?.replace(":", ".")
    );
    const numberOfSlots = Math.ceil(durationTotal / getBookingSpacing.value);
    const amount = numberOfSlots * selectedActivity.value.sessionFullPrice;
    return amount;
  }
  return 0;
});

const couponsAreSafe = () => {
  let canMoveBooking = true
  if(canEditDateAndTimePage()){
    return true
  }
  if(props?.booking?.payMethod?.includes('Coupon')) {
    if (selectedActivity?.value?.couponCostAfterHours) {
      if (selectedActivity?.value?.couponCost != selectedActivity?.value?.couponCostAfterHours) {
        let startMins = 0
        let id = form.value?.time
        if (form.value?.area && getAreaList.value) {
          getAreaList.value.forEach((area: any) => {
            if (area.id == form.value.area) {
              startMins += area?.offSetMinutes || 0
            }
          })
        }

        timeList.value.forEach((time: any) => {
          let offsetTime = time.value.split(":")[0] + ":" + (startMins < 9 ? "0" + startMins : startMins)
          let unOffsetTime = time.value.split(":")[0] + ":00"
          if ((time.value == id || offsetTime == id || unOffsetTime == id) && !time.isNormalHour) {
            canMoveBooking = false
          }
        })
      }
    }
  }
  return canMoveBooking
}

const getCouponValue = () => {
  let couponValue = props?.couponValue || 30
  if(selectedActivity?.value?.sessionFullPrice && selectedActivity?.value?.couponCost) {
    if(!isAfterHours(form.value?.time)){
      couponValue = selectedActivity?.value?.sessionFullPrice / selectedActivity?.value?.couponCost
    } else {
      couponValue = selectedActivity?.value?.sessionFullPrice / (selectedActivity?.value?.couponCostAfterHours ? selectedActivity?.value?.couponCostAfterHours : selectedActivity?.value?.couponCost)
    }
  }
  return couponValue;
}

const getCouponCost = () => {
  let couponCost = selectedActivity?.value?.couponCost || 1
  if(selectedActivity?.value?.couponCost) {
    if(!afterHours.value){
      couponCost = selectedActivity?.value?.couponCost
    } else {
      couponCost = selectedActivity?.value?.couponCostAfterHours ? selectedActivity?.value?.couponCostAfterHours : selectedActivity?.value?.couponCost
    }
  }
  return couponCost;
}

/**
 * Computes and returns the global info message for the booking form.
 *
 * @returns {Object} - The global info message object with properties: color, icon, message, paymentOptions.
 */
const globalInfoMessage = computed(() => {
  const isDue = dueAmount.value > 0;
  isYetToPayDues.value = !!isDue;
  const payOptions =
    haveCreateAccess.value || haveEditAccess.value
      ? paymentOptions.value
      : paymentOptions.value.filter(
          (option) => !["pos", "waiveFee"].includes(option.value)
        );

  let couponList = ""
  if(userHasCoupons.value){
    let couponValue = props?.couponValue ?? 1;
    const todDiscountVal = todAdjustment.value < 0 ? -todAdjustment.value : 0;
    const discount = todDiscountVal > docketDiscount.value ? todDiscountVal : docketDiscount.value || 0;
    let totalDue = dueAmount.value;

    if(selectedActivity?.value?.sessionFullPrice && selectedActivity?.value?.couponCost) {
      if(afterHours.value){
        couponValue = selectedActivity?.value?.sessionFullPrice / (selectedActivity?.value?.couponCostAfterHours ? selectedActivity?.value?.couponCostAfterHours : selectedActivity?.value?.couponCost)
      } else {
        couponValue = selectedActivity?.value?.sessionFullPrice / selectedActivity?.value?.couponCost;
      }
    }

    // If todAdjustment is an increase then add it to the coupon before any discount
    if(todAdjustment.value > 0){
      couponValue = couponValue + couponValue * (todAdjustment.value / 100);
    }

    let couponValueAfterDiscount = parseFloat(((parseFloat(couponValue) * 1 * (100 - discount)) / 100).toFixed(2));

    let productTotal = 0;
    if(selectedProducts.value && selectedProducts.value.length > 0){
      selectedProducts.value.forEach((product:any) => {
        productTotal += product.itemPrice * product.qty;
        totalDue = totalDue - product.itemPrice * product.qty;
      })
    }

    let quantity = Math.round(totalDue / couponValueAfterDiscount)
    let sessionPrice = 0;
    try {
      sessionPrice = parseFloat((selectedActivity?.value?.sessionFullPrice * 1 * (100 - discount)) / 100);
    } catch(e){}
    if(quantity > 0 && totalDue >= sessionPrice) {
      const productExtra = productTotal > 0 ? ` + $${productTotal.toFixed(2)}` : "";
      couponList = "(or " + quantity + " coupon" + (quantity > 1 ? "s" : "") + productExtra + ")"
    }
  }

  const baseMessage = {
    color: "dialogHeaderColor",
    icon: "" as string,
    due: dueAmount.value,
    message: `${couponList}-
    ${selectedActivity?.value?.bookingShortDescription ? selectedActivity?.value?.bookingShortDescription : 'Play or Practice'}
    ${selectedActivity?.value?.maxPlayers > 1 ? ' with up to ' + selectedActivity?.value?.maxPlayers + ' Players' : ''}`,
    todAdjustment: todAdjustment.value || 0,
    paymentOptions: isDue ? payOptions : undefined,
  };

  if (isEditBooking.value) {
    const statusColorName = isDue
      ? bookingStatus.value === "futureBooking"
        ? "warning"
        : "error"
      : "dialogHeaderColor";

    const statusIcon = isDue
      ? bookingStatus.value === "futureBooking"
        ? "tabler-alert-triangle"
        : "tabler-xbox-x"
      : "";

    return {
      ...baseMessage,
      color: statusColorName,
      icon: statusIcon,
      due: '',
      todAdjustment: 0,
      message: isDue
        ? `The Session fee of ${formatPrice(dueAmount.value)} ${couponList} has not been paid yet`
        : props?.booking?.payMethod?.length > 1
        ? `Booking paid by ${props.booking.payMethod.join(", ")}`
        : props?.booking?.payMethod?.length === 1 &&
          props.booking.payMethod[0] === "Fee Waived"
        ? "Booking fee has been waived."
        : props?.booking?.payMethod?.length === 1
        ? `The Session has been fully paid - ${props.booking.payMethod}`
        : "The Session has been fully paid.",
    };
  }
  return baseMessage;
});

const getMaxBookAhead = computed(() => {
  if (pb.authStore.model?.roles?.length > 0 && !isUserMode.value) {
    return 52;
  }
  const location = props?.locationList?.find(
    (location: any) => location?.id === props?.currentLocation
  );
  return recurring?.value?.interval === "Weeks"
    ? location?.maxBookAheadWeeks
    : recurring?.value?.interval === "Months"
    ? location?.maxBookAheadWeeks / 4
    : 0;
});

const isDurationLongerThanOriginal = computed(() => {
  // Check if the selected duration is longer than the original booking duration
  if (!props?.booking?.duration || !form.value.duration) {
    return false;
  }

  const selectedDurationMinutes = mapSelectionToMinutes(
    String(form.value.duration)?.replace(":", ".")
  );
  const originalDurationMinutes = props.booking.duration;

  return selectedDurationMinutes > originalDurationMinutes;
});

// Tracks the drawer and resolved activity so a booking opened straight from a URL
// still fetches availability once the activity list arrives.
watch(
  [
    () => props?.isDrawerOpen,
    () => form.value.date,
    () => selectedActivity.value?.id,
  ],
  async ([isDrawerOpen, date, activityId]) => {
    if (isDrawerOpen && date && activityId) {
      const response = await pb.send('/bbapi/times/getAvailableTimes', {
        method: 'POST',
        body: {
          activity: activityId,
          membership: props?.membership?.id || [],
          date: moment(date).format('YYYY-MM-DD'),
          isAdmin: pb.authStore?.model?.roles?.length > 0,
        },
        requestKey: null,
      });
      availableTimes.value = response?.availableTimes?.length ? [response.availableTimes[0]] : [];
    }
  },
  { immediate: true }
);

watch(
  [
    () => form.value.duration,
    () => form.value.owner,
    () => form.value.time,
    () => form.value.date,
    () => form?.value?.actualDuration,
  ],
  async (newValues, oldValues) => {
    if(props?.isDrawerOpen){
      // If duration changed to a higher value, reset actualDuration to first option (0 extra minutes)
      if(durationUserChanged && newValues[0] !== oldValues?.[0]) {
        const newDurationMinutes = mapSelectionToMinutes(
          String(newValues[0])?.replace(":", ".")
        );
        const oldDurationMinutes = mapSelectionToMinutes(
          String(oldValues?.[0])?.replace(":", ".")
        );
        if(newDurationMinutes > oldDurationMinutes && form.value.actualDuration && durationExtendList.value.length > 0) {
          form.value.actualDuration = durationExtendList.value[0];
        }
      }
      durationUserChanged = true;

      // If owner changed, wait for membership discounts to update first
      if(newValues[1] !== oldValues?.[1]) {
        userMembershipDiscounts.value = await getMembershipDiscounts(form.value?.owner ?? props?.booking?.owner);
        userHasCoupons.value = await doesUserHaveCoupons(form.value.owner, afterHours.value, form.value.activity)
      }
      await fetchPaymentStepData();
    }
  }
);

// Methods
const validateMemberVisitors = async () => {
  /**
   * Validates the number of members and guests selected in the booking form.
   *
   * @returns {Promise<boolean>} - Returns true if the validation is successful, false otherwise.
   */
  // Calculate the number of selected members
  const selectedMembers = form.value.members?.length ?? 0;

  // Calculate the number of selected guests
  const selectedGuests = form.value.guests?.length ?? 0;

  // Check if the total number of selected members and guests is less than the minimum number of players
  if (selectedMembers + selectedGuests < minPlayers.value) {
    // Display an error message if the condition is true
    let message = `You must select at least ${minPlayers.value} players.`;
    handleUpdateErrorMessage(true, message);
    return false;
  }

  // Check if the total number of selected members and guests is greater than the maximum number of players
  if (selectedMembers + selectedGuests > maxPlayers.value) {
    // Display an error message if the condition is true
    let message = `You have selected too many players.`;
    handleUpdateErrorMessage(true, message);
    return false;
  }

  // Clear the error message if the validation is successful
  handleUpdateErrorMessage(false, "");
  return true;
};

const validateTimeOnDay = async () => {
  // Check if the time exists in the timeList by comparing the 'value' property
  const timeExists = timeList.value.some(timeSlot => timeSlot.value === form.value.time);
  const isAdmin = role.value === "Admin" || haveEditAccess.value || haveCreateAccess.value;

  if (form.value.time && !timeExists && !isAdmin) {
    let message = "Time is outside booking hours for this day";
    handleUpdateErrorMessage(true, message);
    return false;
  }
  return true;
}

const validateTermAndCondition = async () => {
  if (isInProgressBooking.value) {
    return true;
  }
  // Check if the user has agreed to the terms
  if (!form.value?.agreement) {
    let message = "You must agree to our booking terms to continue";
    handleUpdateErrorMessage(true, message);
    return false;
  }
  // Record T&C acceptance date only when the booking is for the logged-in user
  if (props.userId && form.value?.owner === props.userId) {
    pb.collection("users").update(props.userId, { acceptedTCs: moment().toISOString() }).catch(console.error);
  }
  // If the terms are agreed, no error message is needed
  handleUpdateErrorMessage(false, "");
  return true;
};

/**
 * Validates the duration of a booking.
 *
 * @returns {Promise<boolean>} - Returns true if the duration is valid, false otherwise.
 */
const validateDuration = async () => {
  if (haveCreateAccess.value) return true;

  const durationTotal = mapSelectionToMinutes(
    String(form.value.duration)?.replace(":", ".")
  );

  const timeRanges = selectedActivity?.value?.expand?.availibleTimes;
  const dayOfWeek = moment(form.value?.date).format("dddd").toLowerCase();
  const minutes = parseInt(durationTotal, 10);
  const { time } = form.value;
  const startTime = moment(time, "HH:mm");
  const endTime = startTime.clone().add(minutes, "minutes");

  // Get available hours for the current day
  const availableHoursForDay = getAvailableHoursForDay(timeRanges, dayOfWeek, form.value?.date);

  // Check if the start and end time are within valid hours
  const isValidTimeForCurrentDay =
    availableHoursForDay.includes(startTime.hour()) &&
    availableHoursForDay.includes(endTime.hour());

  // If the time spans over the midnight (next day)
  let isValidForNextDay = true;

  if (endTime.day() !== startTime.day()) {
    const nextDay = moment(endTime).format("dddd").toLowerCase();
    const availableHoursForNextDay = getAvailableHoursForDay(
      timeRanges,
      nextDay,
      moment(form.value?.date).add(1, "day")
    );
    isValidForNextDay = availableHoursForNextDay.includes(endTime.hour());
  }

  // If either current day or next day is invalid, return false with error message
  if (!isValidTimeForCurrentDay || !isValidForNextDay) {
    handleUpdateErrorMessage(
      true,
      `Sorry, the selected time is outside of our normal operating hours.`
    );
    return false;
  } else if (isInCurrentTimeSlot.value) {
    handleUpdateErrorMessage(
      true,
      `Sorry, the booking is in the current time slot and can't be changed.`
    );
    return false;
  } else {
    handleUpdateErrorMessage(false, "");
    return true;
  }
};

/**
 * Validates the recurring booking form.
 *
 * @returns {Promise<boolean>} - Returns true if the form is valid, false otherwise.
 */
const validateRecurring = async () => {
  // If the booking is not recurring, return true
  if (!form.value?.isRecurring) {
    return true;
  }

  // Destructure the recurring values from the form
  const { interval, endDate, frequency, days } = recurring?.value;

  // Define the required fields and their corresponding error messages
  const requiredFields = [
    {
      field: interval,
      message: "Please select a recurring interval.",
    },
    {
      field: frequency,
      message: "Please specify the recurring frequency.",
    },
    {
      field: days && days?.length > 0,
      message: "Please select at least one recurring day.",
    },
    {
      field: endDate,
      message: "Please specify the recurring end time.",
    },
  ];

  // Find the first missing field
  const missingField = requiredFields.find(({ field }) => !field);

  // If a missing field is found, update the error message and return false
  if (missingField) {
    handleUpdateErrorMessage(true, missingField.message);
    return false;
  }

  // If no missing fields are found, return true
  return true;
};


const checkItemAvailability = async (optionId: string, quantity = 1) => {
  const option = (selectedActivity.value?.expand?.options || []).find(
    (opt: any) => opt.id === optionId
  );
  if (!option) {
    return false;
  }
  
  if (option?.inStock === 0) {
    handleUpdateErrorMessage(true, "Sorry out of stock on this item");
    return false;
  }

  if (option?.hiredOutItem) {
    const bookingStart = moment(`${form.value?.date} ${form.value?.time}`, "YYYY-MM-DD HH:mm").add(1, "seconds");
    const bookingEnd = bookingStart
      .clone()
      .add(Number(form.value?.duration), "minutes")
      .add(-1, "seconds");

    try {
      const bookings = await pb.collection("bookings").getList(1, 50, {
        filter: `startTime < '${bookingEnd
          .utc()
          .format("YYYY-MM-DD HH:mm:ss")}' && endTime > '${bookingStart
          .utc()
          .format("YYYY-MM-DD HH:mm:ss")}'`,
        expand: "docketLine",
      });

      let bookedCount = 0;
      for (const booking of bookings.items) {
        if (booking.options?.length) {
          if (booking.options.includes(optionId)) {
            if (booking.id !== props?.booking?.id && booking.state !== "Deleted") {
              bookedCount += getBookingOptionQuantity(booking.optionQuantities, optionId);
            }
          }
        }
      }

      if (bookedCount + quantity > option.inStock) {
        handleUpdateErrorMessage(
          true,
          "Hire Item not available for the booking time selected"
        );
        return false;
      }
    } catch (error) {
      console.error("Error checking availability:", error);
      return false;
    }
  }
  return true;
};

/**
 * Handles the submission of the booking form.
 * Validates all forms, updates the booking details, and makes an API call to update the booking.
 *
 * @returns {Promise<void>}
 */
const handleSubmit = async () => {
  // Guard against re-entrant submits (e.g. a double-click/tap) racing each
  // other before the loading overlay has a chance to block further clicks.
  if (isSubmitting.value) {
    return;
  }
  isSubmitting.value = true;
  isLoading.value = true;

  try {
    // Validate all forms
    const { valid } = await dateTimeForRef.value?.validate();

    let durationTotal = mapSelectionToMinutes(
      String(form.value.duration)?.replace(":", ".")
    );

    if (!valid) {
      return;
    }

    // Validate member visitors terms conditions and time duration
    if (
      !(await validateMemberVisitors()) ||
      !(await validateTermAndCondition()) ||
      !(await validateDuration()) ||
      !(await validateRecurring()) ||
      !(await validateTimeOnDay())
    ) {
      return;
    }

    selectedDurationSlot.value = durationTotal / getBookingSpacing.value;
    localStorage.removeItem("pendingBookingId")
    await fetchPaymentStepData();

    // Update booking only if nothing has been paid
    let bookingDetails = {
      ...form.value,
      date: form.value?.date,
      due: dueAmount.value,
      value: getBookingAmount.value,
      discountApplied: discountApplied.value,
      lineDiscountTotal: lineDiscountTotal.value,
      todAdjustment: todAdjustment.value,
      todSource: todSource.value,
      docketDiscount: docketDiscount.value,
      id: pendingBookingId.value || form.value.id,
      updatedBy: props?.userId,
      duration: durationTotal,
      actualStartTime: form.value?.actualStartTime
        ? moment(form.value?.actualStartTime, "DD-MM-YYYY hh:mm a").isValid()
          ? moment(
            form.value?.actualStartTime,
            "DD-MM-YYYY hh:mm a"
          ).toISOString()
          : null
        : null,
      actualDuration: props?.booking?.actualDuration
        ? form.value?.actualDuration
        : null,
      slot: selectedDurationSlot.value,
      sessionPrice: selectedActivity?.value?.sessionFullPrice,
      recurring: form.value.isRecurring
        ? {
          id: props?.recurringDetails?.recurringId || recurringBookingId.value,
          isEditAll: props?.recurringDetails?.isEditAll
            ? props?.recurringDetails?.isEditAll
            : false,
          interval: recurring.value?.interval,
          frequency: recurring?.value?.frequency,
          days: recurring.value?.days,
          endDate: moment(recurring?.value?.endDate).toISOString(),
        }
        : {},
      options: form.value.selectedOptions || [],
      optionQuantities: getBookingOptionQuantities(form.value.selectedOptions, form.value.optionQuantities),
    };

    pendingBookingId.value = bookingDetails.id;

    // Has Duration Changed?
    if(addMinuteAmount.value > 0){
      bookingDetails.addMinuteAmount = addMinuteAmount.value
    }

    if(canEditDateAndTimePage() && form.value?.actualStartTime
      && props?.booking?.actualDuration > 0
      && props?.booking?.duration < durationTotal
      && bookingDetails?.actualDuration > 0){
      // Total Slots Needs
      let slots = durationTotal / selectedActivity?.value?.bookingSpacing
      let slotsTaken = props?.booking?.duration / selectedActivity?.value?.bookingSpacing
      // Get New Duration Slot
      let slotsLeft = slots - slotsTaken
      bookingDetails.actualDuration = bookingDetails.actualDuration + (slotsLeft * selectedActivity?.value?.bookingSpacing)
    } else if(canEditDateAndTimePage() && props?.booking?.actualDuration > 0
      && props?.booking?.duration > durationTotal
      && bookingDetails?.actualDuration > 0){
      // Total Slots Needs
      let slots = durationTotal / selectedActivity?.value?.bookingSpacing
      let slotsTaken = props?.booking?.duration / selectedActivity?.value?.bookingSpacing
      // Get New Duration Slot
      let slotsLeft = slotsTaken - slots
      bookingDetails.actualDuration = bookingDetails.actualDuration - (slotsLeft * selectedActivity?.value?.bookingSpacing)
    }

    await updateBooking(bookingDetails);

    emit("reloadBookingList", true);


    if (!errorDateList.value.length) {
      if (dueAmount.value <= 0) {
        const savedBookingId = pendingBookingId.value || form.value.id;
        const isGuestBooking = props?.booking?.source === "Guest Booking";

        // Nothing left to pay, so skip the payment step rather than showing an empty one
        resetForm();
        currentStep.value = 0;
        pendingBookingId.value = null;
        if (isGuestBooking) {
          emit("showConfirmDialog", savedBookingId);
        }
        emit("closeDialog", false);
      } else {
        // Goto payment tab
        currentStep.value = bookingSteps?.value?.length - 1;
      }
    }
  } catch (e: any) {
    const { message } = e?.response;
    if (message) {
      handleUpdateErrorMessage(true, message);
    }
  } finally {
    isLoading.value = false;
    isSubmitting.value = false;
  }
};

const createUpdateRecurringBookings = async () => {
  /**
   * Handles the creation or update of recurring bookings.
   *
   * @returns {Promise<void>}
   */
  const durationTotal = mapSelectionToMinutes(
    String(form.value.duration)?.replace(":", ".")
  );
  selectedDurationSlot.value = durationTotal / getBookingSpacing.value;
  const bookingDetails = {
    ...form.value,
    date: form.value.date,
    due: dueAmount.value,
    value: getBookingAmount.value,
    id: pendingBookingId.value || form.value.id,
    loggedInUser: props?.userId,
    duration: durationTotal,
    actualStartTime: form.value?.actualStartTime
      ? moment(form.value?.actualStartTime).toISOString()
      : null,
    actualDuration: props?.booking?.actualDuration,
    slot: selectedDurationSlot.value,
    sessionPrice: selectedActivity?.value?.sessionFullPrice,
    recurring: form.value.isRecurring
      ? {
        id: props?.recurringDetails?.recurringId || recurringBookingId.value,
        isEditAll: props?.recurringDetails?.isEditAll
          ? props?.recurringDetails?.isEditAll
          : false,
        interval: recurring.value?.interval,
        frequency: recurring?.value?.frequency,
        days: recurring.value?.days,
        endDate: moment(recurring?.value?.endDate).toISOString(),
      }
      : {},
  };

  pendingBookingId.value = bookingDetails.id;

  try {
    await handleRecurringBooking(bookingDetails);
    if (!errorDateList.value.length) {
      // Goto agreement tab
      currentStep.value++;
    }
  } catch (e: any) {
    const { message } = e?.response;
    if (message) {
      handleUpdateErrorMessage(true, message);
    }
  }
};

const getMaxActivityVisitors = () => {
  // Find the selected activity in the activity list
  const activity = props?.activityList?.find(
    (item: any) => item.id === form.value.activity
  );

  // Update the maximum number of players and visitors based on the selected activity
  maxPlayers.value = activity?.maxPlayers;
  minPlayers.value = activity?.minPlayers;
};

const handleMemberUpdate = (newValue: any) => {
  /**
   * Handles the update of the members in the booking form.
   *
   * @param {Array} newValue - The new array of member IDs.
   * @returns {void}
   */
  const message = { isVisible: false, message: "" };

  // Check if an activity is selected
  if (!form.value?.activity) {
    message.isVisible = true;
    message.message = "Please select activity first";
    form.value.members = [];
  } else {
    // Calculate the number of selected guests
    const selectedGuests = form.value?.guests?.length ?? 0;

    // Calculate the maximum allowed number of members
    const maxAllowedMembers = maxPlayers.value - selectedGuests;

    // Check if the total number of members exceeds the allowed limit
    if (newValue.length > maxAllowedMembers) {
      message.isVisible = true;
      message.message = `The total number of Players cannot exceed ${maxPlayers.value}.`;

      // Limit the members array to the maximum allowed number of members
      form.value.members = newValue.slice(0, maxAllowedMembers);
    } else {
      // Update the members array with the new value
      form.value.members = newValue;
    }
  }

  // Call the function to handle the update of the error message
  handleUpdateErrorMessage(message?.isVisible, message?.message);
};

const handleGuestUpdate = (newValue: any) => {
  /**
   * Handles the update of the guests in the booking form.
   *
   * @param {Array} newValue - The new array of guest IDs.
   * @returns {void}
   */
  // Check if an activity is selected
  if (!form.value?.activity) {
    // Clear the guests array and return if no activity is selected
    handleUpdateErrorMessage(false, "");
    form.value.guests = [];
    return;
  }

  // Calculate the number of selected members
  const selectedMembers = form.value?.members ? form.value?.members?.length : 0;

  // Check if the total number of guests and members exceeds the allowed limit
  if (newValue.length + selectedMembers > maxPlayers.value) {
    // Limit the guests array to the maximum allowed number of guests
    form.value.guests = newValue.slice(0, maxPlayers.value - selectedMembers);

    // Display an error message if the limit is exceeded
    let message = `The total number of members and guests cannot exceed ${maxPlayers.value}.`;
    handleUpdateErrorMessage(true, message);
  } else {
    // Update the guests array with the new value
    form.value.guests = newValue;

    // Clear the error message if the limit is not exceeded
    handleUpdateErrorMessage(false, "");
  }
};

const assignRole = () => {
  /**
   * Assigns the role based on the current logged-in user's roles at the selected location.
   *
   * @returns {void}
   */
  // Filter the current logged-in user's roles for the selected location
  let currentLocationRole = props?.currentLoggedInRoles?.filter(
    (role) => role.location === props?.currentLocation
  );

  // If the current logged-in user has roles at the selected location
  if (currentLocationRole?.length) {
    // If the current logged-in user has an admin role at the selected location
    if (currentLocationRole.some((role) => role.name?.includes("Admin"))) {
      // Assign the admin role
      role.value = "Admin";
    } else if (
      // If the current logged-in user has a staff role at the selected location
      currentLocationRole.some((role) => role.name?.includes("Staff"))
    ) {
      // Assign the member role
      role.value = "Member";
    } else {
      // If the current logged-in user has no admin or staff role at the selected location
      // Assign the user role
      role.value = "User";
    }
  } else {
    // If the current logged-in user has no roles at the selected location
    // Assign an empty string as the role
    role.value = "";
  }
};

const resetForm = () => {
  form.value = {
    id: "",
    title: "",
    date: null,
    activity: "",
    guests: [],
    area: null,
    time: null,
    notes: "",
    members: [],
    agreement: false,
    notifyMe: false,
    owner: "",
    isRecurring: false,
    selectedOptions: [],
    visitorEmail: "",
    numberInParty: 1,
  };
};

const confirmCloseBooking = async (cancelReason = 'User Cancelled') => {
  /**
   * Handles the closing of the booking dialog and clears the form data.
   *
   * @returns {Promise<void>}
   */
  // Check and remove any pending state booking
  const cancelledAtStep = bookingSteps.value[currentStep.value]?.title;
  await removeExistingPendingBooking(
    pendingBookingId.value || props?.booking?.id,
    cancelReason,
    cancelledAtStep
  );

  // Clear the form data
  resetForm();

  // Reset the current step to the first step
  currentStep.value = 0;

  // Close the booking dialog
  showPopup.value = false;

  // Clear the pending booking ID
  pendingBookingId.value = null;

  // Clear the recurring booking ID
  recurringBookingId.value = null;

  // Reset the recurring form data
  recurring.value = {
    frequency: 1,
    interval: "Weeks",
    days: [],
    endDate: "",
  };

  // Clear the error date list
  errorDateList.value = [];

  // Close the booking warning dialog
  isBookingWarningDialogVisible.value = false;

  // Emit the closeDialog event with false as the parameter
  emit("closeDialog", false);
};

const closeDialogForPayLater = async () => {
  const bookingId = props?.booking?.id || pendingBookingId.value
  const options = [...(form.value.selectedOptions || [])];
  const optionQuantities = getBookingOptionQuantities(options, form.value.optionQuantities);
  localStorage.removeItem('qbid')
  if(bookingId && (currentLocationDetails.value?.payInStoreEnabled || canEditDateAndTimePage())){
    pendingBookingId.value = bookingId;
    try {
      await pb.collection("bookings").update(bookingId, { state: "Confirmed", options, optionQuantities });
      Object.assign(props.booking, { state: "Confirmed", options, selectedOptions: options, optionQuantities });
      localStorage.setItem('qbid',bookingId)
    } catch(e: any){
      handleUpdateErrorMessage(true, e.message);
      return;
    }
  }
  // Clear the form data
  resetForm();

  // Show booking confirm dialog in parent (closes stepper first)
  emit('showConfirmDialog', bookingId);

  // Reset the current step to the first step
  currentStep.value = 0;

  // Close the booking dialog
  showPopup.value = false;

  // Clear the pending booking ID
  pendingBookingId.value = null;

  // Clear the recurring booking ID
  recurringBookingId.value = null;

  // Reset the recurring form data
  recurring.value = {
    frequency: 1,
    interval: "Weeks",
    days: [],
    endDate: "",
  };

  // Clear the error date list
  errorDateList.value = [];

  // Close the booking warning dialog
  isBookingWarningDialogVisible.value = false;
};


const gotoFood = () => {
  emit("closeDialog", false);

  const location = props?.locationList?.find(
    (location: any) => location?.id === props?.currentLocation
  );
  if(location.bookingFoodLinkCatID){
    router.push('/webshop?b=' + props?.booking?.id + '&categoryid=' + location.bookingFoodLinkCatID)
  } else {
    router.push('/webshop?b=' + props?.booking?.id)
  }
}

const canOrderFood = ref(false)
const selectedPaymentTab = ref('')
const foodHasBeenChecked = ref(false)
const checkIfFoodCanBeOrdered = async () => {
  if(!foodHasBeenChecked.value) {
    try {
      let checkFood = await pb.collection("products").getFullList({filter: 'canBuyInWebshop=true && notDirectlySold=false && location="' + currentLocation.value + '"', expand: 'category'})
      foodHasBeenChecked.value = checkFood.some(item => item?.expand?.category?.isFood);
    } catch (e) {}
  }

  if (foodHasBeenChecked.value) {
    // Get difference in days between now and booking
    const bookingStartDate = moment(props.booking.startDate).startOf('day');
    const today = moment().startOf('day');
    const daysDifference = bookingStartDate.diff(today, 'days');

    canOrderFood.value = canPayInStore() && daysDifference <= currentLocationDetails.value.maxFoodOrderAheadDays;
  }
}

const closeDialog = async () => {
  if (
    (isPendingBooking.value || pendingBookingId.value) &&
    bookingSteps?.value?.length - 1 !== currentStep?.value
    && props?.booking?.state != 'Confirmed'
  ) {
    // show the confirmation message
    isCancelDialogVisible.value = true;
  } else {
    await confirmCloseBooking();
  }
};

// scroll to active hour in list
const handleMenuUpdate = (isOpen: boolean) => {
  if (isOpen) {
    setTimeout(() => {
      const overlayContainer = document?.querySelector(".select-menu-overlay");
      const list = overlayContainer?.querySelector(".v-list");
      if (list) {
        const normalHourElement = Array?.from(list.children)?.find((el) => {
          return el?.classList?.contains("is-normal-hour");
        });
        if (normalHourElement) {
          normalHourElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    }, 200);
  }
};

/**
 * Fetches due and discount data for the payment step.
 * @returns Promise with booking calculation data
 */
const fetchPaymentStepData = async () => {
  try {
    isLoading.value = true;

    if (!selectedActivity.value || !form.value.time){
      return
    }

    // Get docket discount values
    const docketCodeBookings = docketDiscounts.value?.discCodeBookings || 0;
    const docketCodeProducts = docketDiscounts.value?.discCodeProducts || 0;

    // Make the API call
    const response = await pb.send('/bbapi/bookings/getBookingDue', {
      method: 'POST',
      body: {
        membership: userMembershipDiscounts.value,
        user: pb.authStore.model,
        activity: selectedActivity.value,
        date: form.value.date,
        time: form.value.time,
        duration: mapSelectionToMinutes(String(form.value.duration)?.replace(":", ".")),
        options: form.value.selectedOptions || [],
        optionQuantities: getBookingOptionQuantities(form.value.selectedOptions, form.value.optionQuantities),
        discountCodeBookings: docketCodeBookings || discountCodeData.value?.discountBookings || 0,
        discountCodeProducts: docketCodeProducts || discountCodeData.value?.discountProduct || 0,
        docketBookingDiscount: docketDiscounts.value?.discountBookings || 0,
        docketProductDiscount: docketDiscounts.value?.discountProducts || 0,
        alreadyPaid: props?.booking?.paid,
        extraMinutes: addMinuteAmount.value,
      },
      requestKey: null,
    });
    console.log('getBookingDue response', response)
    
    // Handle the response
    todAdjustment.value = response.todAdjustment;
    todSource.value = response.todSource || "";
    docketDiscount.value = response.docketDiscount;
    discountApplied.value = response.discountApplied;
    lineDiscountTotal.value = response.lineDiscountTotal;
    dueAmount.value = response.due;
    bookingTotal.value = response.total;
  } catch (error) {
    console.error('Error fetching payment step data:', error);
    handleUpdateErrorMessage(
      true,
      'Failed to load payment information. Please try again.'
    );
    throw error;
  } finally {
    isLoading.value = false;
  }
};

/**
 * Handles the navigation to the next step in the booking process.
 * Validates the current step's form inputs, updates the current step,
 * and handles the creation or update of bookings.
 *
 * @returns {Promise<void>}
 */
const goToNextStep = async () => {
  try {
    const validateBookingDateTime = async () => {
      // Validate the booking date, time, area, duration, and owner form inputs
      const { valid } = await dateTimeForRef.value.validate();
      if (!(await validateDuration())) {
        return;
      }
      if (valid) {
        if (!isEditBooking.value && !form.value?.isRecurring) {
          try {
            // Make a request to reserve the booking slot
            const response = await reserveSlot(
              form.value?.duration,
              form.value?.date,
              form.value?.time,
              form.value?.activity,
              form.value?.area,
              form.value?.owner,
              props?.userId || form.value?.owner,
              pendingBookingId.value,
              dueAmount.value,
              form.value?.members?.length > 0
                ? form.value?.members
                : [form.value?.owner],
              "Booking",
              undefined,
              (role.value === "Admin" || haveEditAccess.value || haveCreateAccess.value) && !isUserMode.value
            );
            if (response) {
              // Update the pending booking ID
              pendingBookingId.value = response;
              localStorage.setItem("pendingBookingId", response);
              // Move to the next step
              currentStep.value++;
              // Clear the error message
              handleUpdateErrorMessage(false, "");
            }
          } catch (e: any) {
            const { message } = e?.response;
            if (message) {
              handleUpdateErrorMessage(true, message);
            }
          }
        } else {
          // Move to the next step
          currentStep.value++;
        }
        // Add the owner as a member if it's a new booking and has no members already
        if (!isEditBooking.value && form.value.members.length === 0) {
          const owner = form.value?.owner;
          form.value.members = owner && (!Array.isArray(owner) || owner.length > 0) ? [owner] : [];
          recurring.value.days = [getDayIdFromDate(form.value.date)];
        }
      }
    };

    const validateMembersStep = async () => {
      // Validate the members and visitors form inputs
      let { valid } = await memberVisitorsFormRef.value?.validate();
      if (valid) {
        let isMemberVisitorsValid = await validateMemberVisitors();
        if (isMemberVisitorsValid) {
          // Check booking is in confirmed or not
          const { state } = props?.booking;
          if (state !== "Confirmed") {
            // Check booking have any members leftHanded or not
            if (
              isLeftHandWarning.value &&
              !isLeftHandedBookingErrorVerified.value
            ) {
              isLeftHandedWarningDialogVisible.value = true;
              return;
            }
          }

          // Members skip the Date/Time step — if no slot is reserved yet, create the booking now
          const needsSlotReservation = !isEditBooking.value && !pendingBookingId.value;
          if (needsSlotReservation) {
            const stepBefore = currentStep.value;
            await validateBookingDateTime();
            if (currentStep.value === stepBefore) return; // slot reservation failed
          }

          // Save visitor/member details to the booking
          if (pendingBookingId.value) {
            try {
              const userCount = (form.value?.members?.length || 0) + (form.value?.guests?.length || 0);
              form.value.numberInParty = userCount > 0 ? userCount : 1;
              await pb.collection("bookings").update(pendingBookingId.value, {
                users: form.value.members,
                visitorEmail: form.value.visitorEmail,
                visitors: (form.value.guests ?? []).join(","),
                options: form.value.selectedOptions,
                optionQuantities: getBookingOptionQuantities(form.value.selectedOptions, form.value.optionQuantities),
                notes: form.value.notes,
                numberInParty: form.value.numberInParty,
                ...(form.value.members.length > 0 && { termsNotAgreedTo: false }),
              });
            } catch (e: any) {
              console.error(e);
            }
          }
          // Move to the next step (validateBookingDateTime already incremented when it reserved the slot)
          if (!needsSlotReservation) currentStep.value++;
        }
      }
    };

    const validateTermConditionStep = async () => {
      // Validate the term and conditions form inputs
      let { valid } = await agreementFormRef.value?.validate();
      if (valid) {
        let isTermConditionValid = await validateTermAndCondition();
        if (!isTermConditionValid) {
          return;
        }

        // Move to the next step
        currentStep.value++;
      }
    };

    const validateNumberInPartyStep = async () => {
      const { valid } = await numberInPartyFormRef.value?.validate();
      if (valid) {
        if (pendingBookingId.value) {
          try {
            await pb.collection("bookings").update(pendingBookingId.value, {
              numberInParty: form.value.numberInParty,
            });
          } catch (e: any) {
            console.error(e);
          }
        }
        currentStep.value++;
      }
    };

    await fetchPaymentStepData();
    const currentStepId = bookingSteps.value[currentStep.value]?.id;
    if (currentStepId === 1) {
      // Validate the booking date, time, area, duration, and owner form inputs
      await validateBookingDateTime();
    } else if (currentStepId === 2) {
      // Validate the members and visitors form inputs
      await validateMembersStep();
    } else if (currentStepId === 6) {
      // Validate the number in party form input
      await validateNumberInPartyStep();
    } else if (currentStepId === 3) {
      // Validate the recurring configuration form inputs
      let { valid } = await refRecurringVForm.value?.validate();
      if (valid && (await validateRecurring())) {
        await createUpdateRecurringBookings();
      }
    } else if (currentStepId === 4) {
      // Validate the term and conditions form inputs
      await validateTermConditionStep();
    }
  } catch (e) {
    console.error(e)
  }
};

const {
  resetInactivityTimer,
  continueBooking,
  startInactivityTimer,
  clearInactivityTimer,
  clearPendingTimer,
} = useInactivityTimer(
  () => form.value?.id || pendingBookingId.value,
  showPopup,
  () => confirmCloseBooking('Timed out'),
  () => !props?.booking?.state || props?.booking?.state == 'Pending'
);

const gotToPos = (docketId: string) => {
  /**
   * Function to navigate to the POS list screen.
   *
   * @param {string} docketId - The ID of the docket to navigate to.
   *
   * @emits {closeDialog} - Emits an event to close the booking dialog after navigating to the POS list screen.
   *
   * @returns {void}
   */
  // move user to pos list screen
  setTimeout(() => {
    emit("closeDialog", false);
    router.push({
      name: "pos",
      query: {
        did: docketId,
      },
    });
  }, 1000);
};

const handlePaymentSuccess = async () => {
  try {
    await refetchBooking();
    await fetchPaymentStepData();

    let bID = pendingBookingId.value || form.value.id

    setTimeout(async ()=>{
      try{
        // If the dialog was already closed (e.g. by the realtime handleGetBooking listener), bail out
        if (!props.isDrawerOpen) return;

        const booking = await pb.collection("bookings").getOne(bID);

        if (booking) {
          props.booking.due = booking.due
          props.booking.state = booking.state
        }

        if (booking && booking.due === 0 && booking.state == "Confirmed" && canOrderFood.value) {
          isBookingViewOnly.value = true
          currentStep.value = bookingSteps?.value?.length - 2
        } else if(booking && booking.due == 0) {
          currentStep.value = 0;
          emit('showConfirmDialog', bID);
          emit('closeDialog', false);
        }
      } catch(e){}
    },500)
  } catch (e) {}
}

const selectRecurringItem = (item: any) => {
  /**
   * Handles the selection of a recurring booking day.
   *
   * @param {Object} item - The recurring day item to be selected.
   * @returns {void}
   */
  // Get the current selected days from the recurring form
  const { days } = recurring.value || {};

  // Get the maximum number of days that can be selected
  const maxItems = getMaxBookAhead?.value || 0;

  // If the selected day is already in the list and the list is not full, remove it
  if (!days?.includes(item?.id) && days?.length === maxItems) {
    handleUpdateErrorMessage(true, `You can only select up to ${maxItems}.`);
    return;
  }

  // If the selected day is not in the list, add it
  const index = days?.findIndex((i: any) => i === item.id);
  if (index !== -1) {
    recurring.value?.days?.splice(index, 1); // Remove if already selected
  } else {
    recurring.value?.days?.push(item.id); // Add if not selected
  }
};

const handlePaymentDues = async () => {
  isBookingViewOnly.value = false;
  currentStep.value = bookingSteps?.value?.length - 1;
};

const docketDiscounts:any = ref({})
const finishedLoad = ref(false)
const getDocketDiscounts = async (docketBookingId:string) => {
  docketDiscounts.value = {}
  if(docketBookingId){
    try {
      docketDiscounts.value = await pb.collection("dockets").getOne(docketBookingId)
    }catch(e){}
  }
  finishedLoad.value = true
}

const isBookingAfter = ref(false)
const timerBookingCheck = ref(null)
const checkForBookingAfter = (bookingCheck:any) => {
  isBookingAfter.value = false

  if(bookingCheck?.actualStartTime && bookingCheck?.actualStartTime != '' && bookingCheck?.endTime) {
    if(timerBookingCheck.value){
      timerBookingCheck.value = window.clearTimeout(timerBookingCheck.value)
    }
    timerBookingCheck.value = window.setTimeout(async () => {
      try {
        let check = await pb.collection("bookings").getFullList(
          {
            filter:'area="' + bookingCheck?.area + '" && id!="' + bookingCheck?.id + '" && state != "Deleted" && startTime="' +  bookingCheck?.endTime + '"'
         }
        );
        if(check && check.length > 0){
          isBookingAfter.value = true
        }
      } catch (e) {}
    }, 500)
  }
}

const handleGetBooking = async (bookingId: string) => {
  const id = bookingId || pendingBookingId.value;
  const wasAlreadyConfirmed = props.booking?.state === "Confirmed";
  const booking = await pb.collection("bookings").getOne(id, {requestKey: null});
  if ((booking.state === "Confirmed" || booking.due == 0) && !wasAlreadyConfirmed) {
    currentStep.value = 0;
    emit('showConfirmDialog', id);
    emit('closeDialog', false);
  } else if (booking?.due == 0) {
    currentStep.value = 0;
    emit("closeDialog", false);
  }
};

// Watch booking prop
watch(() => props.booking, async (newValue, oldValue) => {
    if (newValue && !isEmpty(newValue)) {
      if (!oldValue || oldValue.id !== newValue.id) {
        durationUserChanged = false;
      }
      // Clone the booking object to avoid direct mutations
      const bookingClone = { ...props.booking };
      // Assign values from booking to form fields
      docketId.value = bookingClone.docketId;
      docketLineId.value = bookingClone.docketLine;
      form.value = {
        id: bookingClone.id,
        activity: bookingClone.activity,
        area: bookingClone.area,
        date: moment(bookingClone.startDate).format("YYYY-MM-DD"),
        time: bookingClone.startDate
          ? moment(bookingClone.startDate).format("HH:mm")
          : null,
        notes: bookingClone.notes,
        members: bookingClone.users ?? (
          (role.value === "Admin" || haveEditAccess.value || haveCreateAccess.value) && !isUserMode.value
            ? []
            : [props.userId]
        ),
        owner: bookingClone.owner || null,
        guests: bookingClone.visitors
          ? bookingClone.visitors
              ?.split(",")
              ?.map((item: string) => item.trim())
          : null,
        duration:
          getBookingSpacing.value === 60
            ? String(bookingClone.duration / getBookingSpacing.value)
            : getDuration(bookingClone.duration, getBookingSpacing.value),
        agreement:
          haveCreateAccess.value || bookingClone?.id || bookingClone?.state === "Confirmed"
            ? true
            : false,
        notifyMe: false,
        isRecurring: !isEmpty(bookingClone.recurring),
        selectedOptions: bookingClone.selectedOptions || [],
        optionQuantities: getBookingOptionQuantities(bookingClone.selectedOptions, bookingClone.optionQuantities),
        visitorEmail: bookingClone.visitorEmail || "",
        numberInParty: bookingClone.numberInParty || 1 
      };

      getDurationList.value.forEach((durObj:any) => {
        let dur = durObj.value;
        let num = ''
        if(form.value.duration.toString().indexOf('.') != -1){ // It is in Decimal
          let parts = form.value.duration.toString().split(".").pop()
          let decimalToMins = parseFloat(parseInt(0) + "." + parts) * 60
          num = form.value.duration.toString().split(".")[0] + ":" + decimalToMins
        }
        if(form.value.duration == dur || form.value.duration + ":00" == dur || num == dur ){
          form.value.duration = dur
        }
      })

      if (bookingClone?.step === 3) {
        currentStep.value = 3;
      }
      
      // Handle "in-progress" status updates
      if (bookingClone.status === "inprogress") {
        form.value.actualStartTime = bookingClone.actualStartTime
          ? moment(bookingClone.actualStartTime).format("DD-MM-YYYY hh:mm a")
          : null;
        form.value.actualDuration = bookingClone.actualDuration || null;

        actualEndTime.value = form.value.actualDuration
          ? moment(form.value.actualStartTime, "DD-MM-YYYY hh:mm a", true)
              .add(form.value.actualDuration, "minutes")
              .format("DD-MM-YYYY hh:mm a")
          : null;
      }

      // Assign booking to pendingBookingId if its state is "Pending"
      if (bookingClone.state === "Pending") {
        pendingBookingId.value = bookingClone.id;
      }

    // Handle recurring booking data
    if (form.value.isRecurring && bookingClone.recurring) {
      recurring.value = {
        endDate: moment(bookingClone.recurring.endDate).format("YYYY-MM-DD"),
        frequency: bookingClone.recurring.frequency,
        interval: bookingClone.recurring.interval,
        days: recurringWeeks.value
          ?.filter((week) =>
            bookingClone.recurring.days
              ?.split(", ")
              ?.map((day: string) => day.trim())
              ?.includes(week.value)
          )
          .map((week) => week.id),
      };
    }

      // Assign additional metadata
      bookingCreatedDate.value = bookingClone.created;

      checkForBookingAfter(bookingClone)
      // Fetch necessary data after assignment
      getMaxActivityVisitors();
      assignRole();
      checkIfFoodCanBeOrdered();
    } else {
      durationUserChanged = false;
      // Reset form for new booking creation
      form.value = {
        id: null,
        date: moment(),
        duration: 1,
        time: null,
        activity: null,
        guests: [],
        area: null,
        notes: "",
        members: (role.value === "Admin" || haveEditAccess.value || haveCreateAccess.value) && !isUserMode.value ? [] : [props.userId],
        owner: (role.value === "Admin" || haveEditAccess.value || haveCreateAccess.value) && !isUserMode.value ? "" : props.userId,
        agreement: haveCreateAccess.value ? true : false,
        notifyMe: false,
        isRecurring: false,
        selectedOptions: [],
        visitorEmail: "",
        numberInParty: 1,
      };
      currentStep.value = 0;
      showPopup.value = false;
      pendingBookingId.value = null;
      isBookingViewOnly.value = false;
    }

  },
  { immediate: true }
);

// Watch getDurationList to sync form.duration when editing a booking
// This handles the race condition where the list isn't fully populated when booking is loaded
watch(
  () => getDurationList.value,
  (newList) => {
    if (!form.value.id || !newList?.length) return;

    // Check if current duration matches any value in the list
    const currentDuration = form.value.duration;
    const matchFound = newList.some((item: any) => item.value === currentDuration);

    if (!matchFound && currentDuration != null) {
      // Try to find a matching value
      for (const durObj of newList) {
        const dur = durObj.value;
        let num = '';
        if (currentDuration.toString().indexOf('.') !== -1) {
          const parts = currentDuration.toString().split('.').pop();
          const decimalToMins = parseFloat('0.' + parts) * 60;
          num = currentDuration.toString().split('.')[0] + ':' + decimalToMins;
        }
        if (currentDuration == dur || currentDuration + ':00' == dur || num == dur) {
          form.value.duration = dur;
          break;
        }
      }
    }
  }
);

/**
 * Watch function to handle changes in form fields related to activity, date,
 * actualDuration, and actualStartTime. It updates the form values, checks for
 * area compatibility, and calculates actualEndTime.
 *
 * @listens {[form.value.activity, form.value.date, form.value.actualDuration, form.value.actualStartTime]}
 *
 * @param {[string, Date, number, string]} [newActivityId, newDate, newDuration, actualStartTime] - The new values of the watched form fields.
 *
 * @emits {form.value.area} - Updates the area value if the new activity is not compatible with the current area.
 * @emits {form.value.time} - Updates the time value if the new date does not have any available time slots.
 * @emits {actualEndTime.value} - Calculates the new actualEndTime value based on the new actualStartTime and actualDuration.
 * @emits {getMaxActivityVisitors()} - Calls the getMaxActivityVisitors function when the new activity changes.
 */
watch(
  () => [
    form.value.activity,
    form.value.date,
    form.value.actualDuration,
    form.value.actualStartTime,
  ],
  ([newActivityId, newDate, newDuration, actualStartTime]) => {
    nextTick(() => {
      // Watch for activity changes
      if (newActivityId) {
        const area = props?.areaList?.find((el) => el.id === form.value.area);
        if (area && !area.activities.includes(newActivityId)) {
          form.value.area = null;
        }
      }

      // Watch for actualDuration and actualStartTime changes
      if (newDuration && actualStartTime) {
        actualEndTime.value = moment(
          actualStartTime,
          "DD-MM-YYYY hh:mm a",
          true
        )
        .add(newDuration, "minutes")
        .format("DD-MM-YY hh:mm a");
      }

      // Get maximum activity visitors when activity changes
      if (newActivityId) {
        getMaxActivityVisitors();
      }
    });
  }
);

/**
 * Watch function to handle changes in the globalErrorMessage.value.
 * It clears the error message after 2 seconds if it is visible.
 *
 * @listens {globalErrorMessage.value}
 *
 * @param {Object} newError - The new value of the globalErrorMessage.value.
 * @param {boolean} newError.isVisible - Indicates whether the error message is visible.
 *
 * @emits {handleUpdateErrorMessage(false, "", "", "")} - Calls the function to clear the error message.
 */
watch(
  () => globalErrorMessage.value,
  (newError) => {
    if (newError?.isVisible) {
      // clear after 2 sec
      setTimeout(() => {
        handleUpdateErrorMessage(false, "", "", "");
      }, 3000);
    }
  },
  { immediate: true }
);

/**
 * Watch function to handle changes in the isLeftHandWarning.value.
 * It checks if a left-handed warning should be displayed and updates the error message accordingly.
 *
 * @listens {isLeftHandWarning.value}
 *
 * @param {boolean} newValue - The new value of the isLeftHandWarning.value.
 *
 * @emits {handleUpdateErrorMessage(true, "A member is left handed but this bay cannot do left handed", "warning", "tabler-alert-triangle")}
 * - Calls the function to update the error message when a left-handed warning should be displayed.
 */
watch(
  () => isLeftHandWarning.value,
  (newValue) => {
    // red message is higher priority so check before show warning messages
    if (
      newValue &&
      bookingSteps.value?.length - 1 !== currentStep.value &&
      globalInfoMessage?.value?.color !== "error"
    ) {
      handleUpdateErrorMessage(
        true,
        "A member is left handed but this bay cannot do left handed",
        "warning",
        "tabler-alert-triangle"
      );
    }
  },
  { immediate: true }
);

const docketNumber:any = ref([])

watch(
  () => props?.isDrawerOpen,
  async (newValue) => {
    userHasCoupons.value = await doesUserHaveCoupons(form.value.owner, afterHours.value, form.value.activity)
    finishedLoad.value = false

    if (isEditBooking.value) {
      isBookingViewOnly.value = true;
      localStorage.setItem('onpayment','1')
    }

    if (newValue && props?.booking?.id) {
      // fetch docket data
      const docket = await pb
        .collection("docketLines")
        .getFullList({filter: 'booking="' + props?.booking?.id + '"', expand:"docket"});

      docketNumber.value = docket
      form.value.selectedOptions = props?.booking?.options ||  []; //productsLine?.filter(el => props?.booking?.options && props?.booking?.options.includes(el.product)).map((el: any) => el?.product);
      form.value.optionQuantities = getBookingOptionQuantities(form.value.selectedOptions, props?.booking?.optionQuantities);
      discountCode.value = docket[docket.length - 1]?.expand?.discountCode;
    } else{
      docketNumber.value = []
    }

    await getUserCreditCards();
    userMembershipDiscounts.value = await getMembershipDiscounts(form.value?.owner ?? props?.booking?.owner);
    await getDocketDiscounts(props?.booking?.docketId)

    // Fetch payment data after membership discounts are loaded
    if(newValue) {
      await fetchPaymentStepData();
    }
  }
);

/**
 * Watch to handle drawer open/close, resetting form data,
 * and managing timers for inactivity.
 *
 * @listens {props.isDrawerOpen} - Listens to the drawer open/close state.
 * @listens {isEditBooking.value} - Listens to the edit booking state.
 * @listens {isPendingBooking.value} - Listens to the pending booking state.
 * @listens {showPopup.value} - Listens to the show popup state.
 * @listens {getAreaList.value} - Listens to the area list state.
 *
 * @emits {currentStep.value} - Emits the current step value.
 * @emits {clearPendingTimer()} - Emits the function to clear the pending timer.
 * @emits {isCancelDialogVisible.value} - Emits the cancel dialog visibility state.
 * @emits {isPendingBooking.value} - Emits the pending booking state.
 * @emits {docketId.value} - Emits the docket ID value.
 * @emits {docketLineId.value} - Emits the docket line ID value.
 * @emits {pendingBookingId.value} - Emits the pending booking ID value.
 * @emits {resetInactivityTimer()} - Emits the function to reset the inactivity timer.
 * @emits {startInactivityTimer()} - Emits the function to start the inactivity timer.
 * @emits {alternativeAreaList.value} - Emits the alternative area list value.
 */
watch(
  () => props?.isDrawerOpen,
  (newValue) => {
    if (!newValue) {
      currentStep.value = 0;
      clearPendingTimer();
      isCancelDialogVisible.value = false;
      isPendingBooking.value = false;
      docketId.value = "";
      docketLineId.value = "";
      pendingBookingId.value = null;
      isLeftHandedBookingErrorVerified.value = false;
      discountCode.value = "";
      discountCodeData.value = {};
      isBookingViewOnly.value = false;
    } else if (!props?.booking?.id) {
      isBookingViewOnly.value = false;
      form.value.members = (role.value === "Admin" || haveEditAccess.value || haveCreateAccess.value) && !isUserMode.value
        ? []
        : [props.userId];
    }
  }
);

watchEffect(() => {
  if (!isEditBooking.value && isPendingBooking.value) {
    resetInactivityTimer();
    const resetEvents = ["mousemove", "keydown", "touchstart", "click"];
    resetEvents.forEach((event) =>
      window.addEventListener(event, resetInactivityTimer)
    );
  }

  if (!showPopup.value) {
    clearInactivityTimer();
  } else {
    startInactivityTimer();
  }

  if (getAreaList.value) {
    alternativeAreaList.value = isLeftHandWarning?.value
      ? getAreaList.value?.filter((el) => el?.canLeftHanded)
      : getAreaList.value || [];
  }
});

const canEditBooking = () => {
  if(canEditDateAndTimePage()){
    return true
  }
  if(props?.booking?.startDate && selectedActivity.value?.changeHours){
    let nowDate = new Date()
    nowDate.setHours(nowDate.getHours() + selectedActivity.value?.changeHours)
    if(new Date(props?.booking?.startDate).getTime() >= nowDate.getTime()){
      return true
    }
  }
  return false
}

const canPayInStore = () => {
  if(selectedActivity?.value?.expand?.availibleTimes && props?.booking?.startDate) {
    const timeRanges = selectedActivity?.value?.expand?.availibleTimes;
    const dayOfWeek = moment(props.booking.startDate)
      .format("dddd")
      .toLowerCase();

    const startTime = moment(new Date(props.booking.startDate), "HH:mm");

    const availableHoursForDay = getAvailableHoursForDay(timeRanges.filter((tr: any) => tr.allUsers === true), dayOfWeek, props.booking.startDate)

    // remove Last Time Slot
    if(availableHoursForDay.length > 1) {
      availableHoursForDay.pop()
    }
    // Check if the start and end time are within valid hours
    const isValidTimeForCurrentDay = availableHoursForDay.includes(startTime.hour())
    return (currentLocationDetails?.value?.payInStoreEnabled && isValidTimeForCurrentDay) || canEditDateAndTimePage()
  } else {
    return currentLocationDetails?.value?.payInStoreEnabled || canEditDateAndTimePage()
  }
}

const updateDuration = () =>{
  if(getDurationList.value[0]) {
    let valid = false
    getDurationList.value.forEach(dr => {
      if(dr.value == form.value.duration){
        valid = true
      }
    })
    if(!valid) {
      form.value.duration = getDurationList.value[0]?.value
    }
  }
}

const updateBookedTime = (item:any) => {
  form.value.area = item
  let closestMatch = form.value.time.toString()
  timeList.value.forEach((time: any) => {
    try {
      let parts = time.value.toString().split(":")[0]
      let matchCompare = closestMatch.split(":")[0]
      if (parts == matchCompare) {
        closestMatch = time.value;
      }
    } catch (e) {}
  })
  form.value.time = closestMatch
}


const uniqueDocketNumber = () => {
  const docketMap = new Map();
  const dockets = docketNumber.value.filter((dn: any) => dn?.expand?.docket);
  
  dockets.forEach((dn: any) => {
    const docketId = dn.expand.docket.id;
    
    if (!docketMap.has(docketId)) {
      // First occurrence of this docket ID
      docketMap.set(docketId, dn);
    } else {
      // If current item is 'booking payment', replace the existing one
      if (dn.transactionType === 'Booking Payment') {
        docketMap.set(docketId, dn);
      }
      // If existing is already 'booking payment' and current is not, keep existing
      // If neither is 'booking payment', keep the first one (existing)
    }
  });
  return Array.from(docketMap.values());
}

const docketinReceipt = ref('')
const showReceiptDialog = ref(false)
const openReceipt = (docketid:string) => {
  docketinReceipt.value = docketid
  showReceiptDialog.value = true
}

defineExpose({
  getDurationList,
  getAreaList,
  getBookingAmount,
  isLeftHandWarning,
  isDurationLongerThanOriginal,
  isOutHourWarningVisible,
  validateDuration,
  validateTimeOnDay,
})
</script>

<template>
  <VDialog
    v-model="props.isDrawerOpen"
    temporary
    persistent
    no-click-animation
    location="end"
    width="900"
    class="scrollable-content booking_stepper">
    <VOverlay
      v-model="isLoading"
      contained
      persistent
      class="align-center justify-center"
    >
      <VProgressCircular indeterminate />
    </VOverlay>
    <!-- Timeout error dialog -->
    <TimeOutError
      :is-visible="showPopup"
      @closeDialog="closeDialog"
      @continueBooking="continueBooking"
    />

    <!-- leftHandedWarning dialog -->
    <WarningDialog
      :is-visible="isLeftHandedWarningDialogVisible"
      @closeDialog="isLeftHandedWarningDialogVisible = false"
      @verifiedByUser="() => {
        isLeftHandedBookingErrorVerified = true;
        isLeftHandedWarningDialogVisible = false;
      }"
    />

    <VCard height="auto" flat>
      <div
        class="d-flex align-center justify-space-between bg-dialogHeaderColor"
      >
        <!-- 👉 Header -->
        <VCardTitle>
          {{
            isBookingViewOnly
              ? `${selectedActivity?.name} Booking Details`
              : isEditBooking && !isBookingViewOnly
                ? "Update " + (selectedActivity?.name ? selectedActivity?.name : '') + " Booking"
                : "Add " + (selectedActivity?.name ? selectedActivity?.name : '') + " Booking"
          }}
        </VCardTitle>

        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          :disabled="currentStep === bookingSteps.length - 1"
          @click="closeDialog"
        />
      </div>

      <VCardSubtitle
        v-if="form.id && props?.booking?.status !== 'futureBooking'"
        class="pa-5 pb-0 font-weight-medium"
      >
        Options limited as booking {{ props?.booking?.status === "completed" ? "completed" : "started" }}
      </VCardSubtitle>

      <!-- Readonly Booking Data -->
      <BookingOverview
        v-if="isBookingViewOnly"
        :booking="props.booking"
        :role="role"
        :activity="selectedActivity"
        :areaList="props.areaList"
        :memberList="props.memberList"
        :products="selectedProducts"
        :canMessage="canEditDateAndTimePage()"
        :haveEditAccess="haveEditAccess"
        :haveCreateAccess="haveCreateAccess"
        :debugUser="props.debugUser"
      />
      <!-- Readonly Booking Data Ends -->

      <!-- Stepper Edit Content  -->
      <VCardText v-else>
        <VRow>
          <!-- large screen stepper -->
          <VCol cols="12" class="stepper-form-header">
            <CustomStepper
              v-model:current-step="currentStep"
              :items="bookingSteps"
              :is-active-step-valid="!isEditBooking ? false : undefined"
              class="stepper-icon-step-bg"
              direction="horizontal"
            />
          </VCol>
          <VCol cols="12" class="pt-0">
            <VWindow v-model="currentStep" class="disable-tab-transition" :touch="false" disabled>
              <VForm ref="dateTimeForRef">
                <VWindowItem class="mt-2" v-if="canEditDateAndTimePage()">
                  <VRow class="pa-0">
                    <VCol
                      sm="6"
                      cols="12"
                      class="pa-0 pl-4 d-flex align-center"
                    >
                      <span class="booking_stepper__explanation">
                        Choose the booking {{ getAreaSingular }}, Time, and Duration.
                      </span>
                    </VCol>
                    <VCol
                      sm="6"
                      cols="12"
                      class="pa-0 pr-3"
                    >
                      <div
                        class="d-flex justify-end align-center float-right booking_stepper__recurring"
                      >
                        <VSwitch
                          v-model="form.isRecurring"
                          label="Make Recurring"
                        />
                      </div>
                    </VCol>
                  </VRow>
                  <VRow>
                    <!-- 👉 Activity -->
                    <VCol md="3" xs="6" cols="12">
                      <VSelect
                        v-model="form.activity"
                        :items="props?.activityList?.filter(ac => !ac?.futureBookingDisabled)"
                        :rules="[requiredValidator]"
                        :disabled="areaDurationDisable"
                        eager
                        item-title="name"
                        item-value="id"
                        label="Activity"
                        placeholder="Select Activity"
                        :on-update="updateDuration()"
                      />
                    </VCol>
                    <!-- 👉 Area -->
                    <VCol sm="4" cols="12">
                      <VSelect
                        @update:model-value="updateBookedTime"
                        v-model="form.area"
                        :items="getAreaList"
                        :rules="[requiredValidator]"
                        :disabled="areaDurationDisable"
                        eager
                        :item-title="area => getAreaOptionLabel(area, selectedActivity, getAreaSingular)"
                        item-value="id"
                        :label="areaSingularForActivity(selectedActivity, getAreaSingular)"
                        :placeholder="`Select ${areaSingularForActivity(selectedActivity, getAreaSingular)}`"
                      />
                    </VCol>

                    <!-- 👉 Date -->
                    <VCol sm="5" cols="12">
                      <DateInput
                        v-if="form?.date && moment(form.date).isSameOrAfter(moment(), 'day')"
                        v-model="form.date"
                        :disabled="isFieldsDisabled || (props?.booking?.payMethod?.includes('Coupon') && !canEditDateAndTimePage())"
                        :rules="[requiredValidator]"
                        label="Date"
                        placeholder="Select Date"
                        format="dddd, Do MMM YYYY"
                        :allowedDates="(date) => allowedDatesRecurring(date, getMaxBookAhead)"
                      />
                      <DateInput
                        v-else
                        v-model="form.date"
                        :disabled="true"
                        :rules="[requiredValidator]"
                        label="Date"
                        placeholder="Date Locked"
                        format="dddd, Do MMM YYYY"
                      />
                    </VCol>

                    <!-- 👉 Start Time -->
                    <VCol lg="3" md="3" sm="3" cols="6">
                      <VSelect
                        v-model="form.time"
                        :items="timeList.filter(tl => {
                          if(couponsAreSafe()){
                            return true
                          } else {
                            return !tl.isNormalHour
                          }
                        })"
                        :rules="[requiredValidator]"
                        :disabled="isFieldsDisabled"
                        :menu-props="{ contentClass: 'select-menu-overlay' }"
                        item-title="label"
                        item-value="value"
                        label="Booked Time"
                        placeholder="Select Time"
                        class="dynamic_time_list"
                        @update:menu="handleMenuUpdate"
                      >
                        <template v-slot:item="{ props, item }">
                          <VListItem
                            v-bind="props"
                            :class="{
                              'is-normal-hour': item.raw.isNormalHour,
                              dark_theme: $vuetify.theme.current.dark,
                              light_theme: !$vuetify.theme.current.dark,
                              restricted_hours: !item.raw.isNormalHour,
                            }"
                            :style="{
                              backgroundColor: item.raw.color,
                              color: $vuetify.theme.current.dark ? '#fff' : '',
                            }"
                          >
                          </VListItem>
                        </template>
                      </VSelect>
                    </VCol>

                    <!-- 👉 Duration -->
                    <VCol lg="4" md="4" sm="4" xs="6" cols="6">
                      <VSelect
                        v-model="form.duration"
                        :items="getDurationList"
                        :disabled="areaDurationDisable"
                        :rules="[requiredValidator]"
                        label="Duration"
                        item-title="text"
                        item-value="value"
                        placeholder="Select Duration"
                      />
                    </VCol>
                    <!-- have create access show them to set or update  -->
                    <VCol md="5" sm="6" cols="12">
                      <VAutocomplete
                        @update:model-value="getFormBookingOwner($event)"
                        v-model="form.owner"
                        :items="props?.memberList"
                        :disabled="isFieldsDisabled"
                        chips
                        closable-chips
                        item-title="firstName"
                        item-value="id"
                        color="blue-grey-lighten-2"
                        label="Owner"
                        class="member_list_selection"
                        @click.prevent="
                          () => {
                            if (!props?.memberList?.length) {
                              emit('loadUsersList', true);
                            }
                          }
                        "
                      >
                        <template v-slot:chip="{ props, item }">
                          <UserChips
                            :bind="props"
                            :item="item"
                            :baseUrl="pb.baseUrl"
                            @removeUser="form.owner = []"
                          />
                        </template>

                        <template v-slot:item="{ props, item }">
                          <UserListItem
                            :bind="props"
                            :item="item"
                            :baseUrl="pb.baseUrl"
                          />
                        </template>
                      </VAutocomplete>
                    </VCol>
                    <VCol
                      v-if="
                        (haveEditAccess || haveCreateAccess) &&
                        isInProgressBooking
                      "
                    >
                      <VRow>
                        <VCol
                          v-if="isInProgressBooking"
                          md="3"
                          sm="6"
                          cols="12"
                        >
                          <!-- actual start time -->
                          <VTextField
                            :model-value="form.actualStartTime ? moment(form.actualStartTime, 'DD-MM-YYYY hh:mm a').format('h:mma') : ''"
                            readonly
                            label="Start time"
                          />
                        </VCol>
                        <VCol
                          v-if="isInProgressBooking"
                          md="4"
                          sm="6"
                          cols="12"
                        >
                          <!-- actual end time -->
                          <VTextField
                            :model-value="actualEndTime ? moment(actualEndTime, 'DD-MM-YYYY hh:mm a').format('h:mma') : ''"
                            readonly
                            label="End time"
                          />
                        </VCol>
                        <VCol
                          v-if="isInProgressBooking"
                          md="5"
                          sm="6"
                          cols="12"
                        >
                          <!-- end time -->
                          <VSelect
                            :disabled="!docketId || docketId == '' || isDurationLongerThanOriginal"
                            v-model="form.actualDuration"
                            :items="durationExtendListMap"
                            :readonly="!haveCreateAccess || !haveEditAccess"
                            label="Actual duration"
                          />
                        </VCol>
                        <VCol v-if="haveCreateAccess && haveEditAccess && (!docketId || docketId == '')"><VAlert color="info">
                          Before adjusting extra minutes - the booking must have a docket. To create an unpaid docket, use "Pay From POS" to create a docket first.
                        </VAlert></VCol>
                      </VRow>
                    </VCol>
                  </VRow>
                </VWindowItem>
              </VForm>
              <VForm ref="memberVisitorsFormRef">
                <VWindowItem>
                  <div class="booking_stepper__explanation mb-6" :class="selectedActivity?.maxPlayers > 1 ? '' : 'd-none'">
                    Select the Players
                  </div>
                  <VRow>
                    <VCol v-if="!canEditDateAndTimePage()" :xs="3" cols="12">
                      <VSelect
                        v-model="form.duration"
                        :items="getDurationList"
                        :disabled="areaDurationDisable"
                        :rules="[requiredValidator]"
                        label="Duration"
                        item-title="text"
                        item-value="value"
                        placeholder="Select Duration"
                      />
                    </VCol>
                    <VCol 
                      :sm="!canEditDateAndTimePage() ? '4' : '6'"
                      cols="12"
                      :class="selectedActivity?.maxPlayers > 1 ? '' : 'd-none'"
                    >
                      <VAutocomplete
                        v-model="form.members"
                        :items="filteredMemberList"
                        :disabled="isFieldsDisabled"
                        chips
                        closable-chips
                        multiple
                        item-title="firstName"
                        item-value="id"
                        :filter-keys="['title', 'raw.lastName', 'raw.username']"
                        color="blue-grey-lighten-2"
                        label="Members"
                        hint="Type 3 characters to search..."
                        persistent-hint
                        class="member_list_selection"
                        :search="memberSearchQuery"
                        @update:search="memberSearchQuery = $event ?? ''"
                        @update:modelValue="handleMemberUpdate"
                        @click.prevent="
                          () => {
                            if (!props?.memberList?.length) {
                              emit('loadUsersList', true);
                            }
                          }
                        "
                      >
                        <template v-slot:chip="{ props, item }">
                          <UserChips
                            :bind="props"
                            :item="item"
                            :baseUrl="pb.baseUrl"
                            v-if="item.raw.id"
                            @click="
                              (event: any) => {
                                if (haveCreateAccess) {
                                  event.stopPropagation();
                                  router.push(`user/${item?.raw?.id}`);
                                }
                              }
                            "
                          />
                        </template>

                        <template v-slot:item="{ props, item }">
                          <UserListItem
                            :bind="props"
                            :item="item"
                            :baseUrl="pb.baseUrl"
                          />
                        </template>
                      </VAutocomplete>
                    </VCol>

                    <!-- 👉 Guests -->
                    <VCol
                      :sm="!canEditDateAndTimePage() ? '5' : '6'"
                      cols="12"
                      :class="selectedActivity?.maxPlayers > 1 ? '' : 'd-none'"
                    >
                      <VCombobox
                        v-model="form.guests"
                        :items="[]"
                        :disabled="isFieldsDisabled"
                        multiple
                        chips
                        closable-chips
                        label="Guest Names"
                        placeholder="Enter guest Names seperated by commas ','"
                        @update:modelValue="handleGuestUpdate"
                      >
                        <template v-slot:selection="data">
                          <VChip
                            v-bind="data.attrs"
                            :key="JSON.stringify(data.item)"
                            :disabled="data.disabled"
                            :model-value="data.selected"
                            size="small"
                            @click:close="data.parent.selectItem(data.item)"
                          >
                            <template v-slot:prepend>
                              <VAvatar class="bg-accent text-uppercase" start>{{
                                data?.item?.title?.slice(0, 1)
                              }}</VAvatar>
                            </template>
                            {{ data?.item?.title }}
                          </VChip>
                        </template>
                      </VCombobox>
                    </VCol>

                    <!-- 👉 Notify Email (shown only when no owner or members) -->
                    <VCol
                      v-if="form.guests?.length > 0 && !form.owner && (!form.members || form.members.length === 0)"
                      cols="12"
                      :class="selectedActivity?.maxPlayers > 1 ? '' : 'd-none'"
                    >
                      <VTextField
                        v-model="form.visitorEmail"
                        label="Notify email"
                        placeholder="Enter email to notify"
                        type="email"
                        :disabled="isFieldsDisabled"
                      />
                    </VCol>
                    <VCol v-if="productList?.length && canHire(form?.time)" cols="12" >
                      <div>
                        <div class="text-subtitle-1 mb-2">
                          Available Extras:
                        </div>
                        <BookingExtras
                          v-model:selected-options="form.selectedOptions"
                          v-model:option-quantities="form.optionQuantities"
                          :options="productList"
                          :disabled="isFieldsDisabled"
                          :check-availability="checkItemAvailability"
                          @change="fetchPaymentStepData"
                        />
                      </div>
                    </VCol>
                    <v-col v-else cols="12">
                      <div class="text-subtitle-1">
                        {{ !productList?.length ? "No Extras available" : "No Extras available during unstaffed hours" }}
                      </div>
                    </v-col>
                    <!-- 👉 Description -->
                    <VCol cols="12" :class="canEditDateAndTimePage() ? '' :'d-none'">
                      <VTextarea
                        v-model="form.notes"
                        :disabled="isFieldsDisabled"
                        label="Notes"
                        placeholder="Booking Notes"
                      />
                    </VCol>
                  </VRow>

                  <VRow v-if="!canEditDateAndTimePage()">
                    <VCol cols="12">Edit Booking Details</VCol>
                      <!-- 👉 Area -->
                      <VCol sm="3" cols="12">
                        <VSelect
                          v-model="form.area"
                          :items="getAreaList"
                          :rules="[requiredValidator]"
                          :disabled="areaDurationDisable"
                          eager
                          :item-title="area => getAreaOptionLabel(area, selectedActivity, getAreaSingular)"
                          item-value="id"
                          :label="areaSingularForActivity(selectedActivity, getAreaSingular)"
                          :placeholder="`Select ${areaSingularForActivity(selectedActivity, getAreaSingular)}`"
                        />
                      </VCol>

                    <!-- 👉 Date -->
                    <VCol sm="5" cols="12">
                      <DateInput
                        v-if="form?.date && moment(form.date).isSameOrAfter(moment(), 'day')"
                        v-model="form.date"
                        :disabled="isFieldsDisabled"
                        :rules="[requiredValidator]"
                        format="dddd, Do MMM YYYY"
                        label="Date"
                        placeholder="Select Date"
                        :allowedDates="(date) => allowedDatesRecurring(date, getMaxBookAhead)"
                      />

                      <DateInput
                        v-else
                        v-model="form.date"
                        :disabled="true"
                        :rules="[requiredValidator]"
                        label="Date"
                        placeholder="Date Locked"
                        format="dddd, Do MMM YYYY"
                      />
                    </VCol>

                      <!-- 👉 Start Time -->
                      <VCol sm="4" cols="12">
                        <VSelect
                          v-model="form.time"
                          :items="timeList.filter(tl => {
                          if(couponsAreSafe()){
                            return true
                          } else {
                            return !tl.isNormalHour
                          }
                        })"
                          :rules="[requiredValidator]"
                          :disabled="isFieldsDisabled"
                          :menu-props="{ contentClass: 'select-menu-overlay' }"
                          item-title="label"
                          item-value="value"
                          label="Booked Time"
                          placeholder="Select Time"
                          class="dynamic_time_list"
                          @update:menu="handleMenuUpdate">
                          <template v-slot:item="{ props, item }">
                            <VListItem
                              v-bind="props"
                              :class="{
                              'is-normal-hour': item.raw.isNormalHour,
                              dark_theme: $vuetify.theme.current.dark,
                              light_theme: !$vuetify.theme.current.dark,
                              restricted_hours: !item.raw.isNormalHour,
                            }"
                              :style="{
                              backgroundColor: item.raw.color,
                              color: $vuetify.theme.current.dark ? '#fff' : '',
                            }">
                            </VListItem>
                          </template>
                        </VSelect>
                        <VAlert v-if="!couponsAreSafe()" class="mt-1">Booking times are limited</VAlert>
                      </VCol>
                    </VRow>
                </VWindowItem>
              </VForm>
              <VWindowItem v-if="bookingSteps?.some((el) => el.id === 6)">
                <NumberInPartyStep ref="numberInPartyFormRef" :form="form" />
              </VWindowItem>
              <VForm ref="refRecurringVForm">
                <VWindowItem v-if="bookingSteps?.some((el) => el.id === 3)">
                  <div class="mb-6">Select the Recurring configuration</div>
                  <VRow>
                    <VCol lg="3" md="3" sm="3" xs="6" cols="12">
                      <VTextField
                        v-model="recurring.frequency"
                        :disabled="isFieldsDisabled"
                        :rules="[requiredValidator]"
                        label="Repeat"
                      />
                    </VCol>

                    <!-- 👉 Interval -->
                    <VCol lg="4" md="4" sm="3" xs="6" cols="12">
                      <VSelect
                        v-model="recurring.interval"
                        :items="['Weeks', 'Months']"
                        :disabled="isFieldsDisabled"
                        :rules="[requiredValidator]"
                        label="Interval"
                        @update:modelValue="recurring.days = []"
                      >
                      </VSelect>
                    </VCol>
                  </VRow>
                  <VRow>
                    <VCol cols="12">
                      <VBtn
                        v-for="item in recurringWeeks"
                        icon=""
                        :key="item.id"
                        :color="
                          recurring?.days?.includes(item?.id)
                            ? 'primary'
                            : 'grey-400'
                        "
                        size="small"
                        class="rounded ml-1"
                        @click="selectRecurringItem(item)"
                      >
                        {{ item.label }}
                        <VTooltip
                          activator="parent"
                          location="top"
                          open-delay="500"
                        >
                          {{ item?.value }}
                        </VTooltip>
                      </VBtn>
                    </VCol>
                  </VRow>
                  <VRow>
                    <VCol sm="5" cols="6">
                      <DateInput
                        v-if="!recurring.endDate || moment(recurring.endDate).isSameOrAfter(moment(), 'day')"
                        v-model="recurring.endDate"
                        :disabled="isFieldsDisabled"
                        :rules="[requiredValidator]"
                        format="dddd, Do MMM YYYY"
                        label="Ends on"
                        placeholder="Ends on"
                        :allowedDates="(date) => allowedDatesRecurring(date, getMaxBookAhead)"
                      />

                      <VTextField
                        v-else
                        v-model="recurring.endDate"
                        readonly
                        :rules="[requiredValidator]"
                        append-icon="mdi-date"
                        label="ENDS ON"
                        placeholder="ENDS ON"
                      />
                    </VCol>
                  </VRow>
                </VWindowItem>
              </VForm>
              <VForm ref="agreementFormRef">
                <VWindowItem v-if="bookingSteps?.some((el) => el.id === 4)">
                  <div class="booking_stepper__explanation mb-6">
                    Review and agree to the terms and conditions
                  </div>
                  <VCheckbox v-model="form.agreement">
                    <template #label>
                      <div class="booking_stepper__agreement-message">
                        {{ getAgreementMessage }}
                        <a
                          class="text-primary"
                          @click.stop.prevent="showTermsConditionsDialog = true"
                          v-if="globalSetup.generalTermsAndConditions"
                        >
                          Terms and conditions.
                        </a>
                      </div>
                    </template>
                  </VCheckbox>
                  <TermsAndConditionsDialog
                    :model-value="showTermsConditionsDialog"
                    :htmlContent="globalSetup.generalTermsAndConditions"
                    @close="showTermsConditionsDialog = false"
                    @accept="form.agreement = true"
                    @decline="form.agreement = false; showTermsConditionsDialog = false"
                  />
                </VWindowItem>
              </VForm>
              <VForm ref="fourthFormRef">
                <VWindowItem>
                  <div v-if="dueAmount > 0 && currentStep == bookingSteps.length -1">
                    <PaymentForm
                      :have-create-permission="haveCreateAccess"
                      :logged-in-user-id="props.userId"
                      :products="selectedProducts"
                      :total-due="dueAmount"
                      :total-paid="props?.booking?.paid"
                      :lineDiscountTotal="lineDiscountTotal"
                      :booking-id="props?.booking.id"
                      :coupon-value="getCouponValue()"
                      :coupon-cost="getCouponCost()"
                      :location="selectedActivity?.location"
                      :user-credit-cards="userCreditCards"
                      :todAdjustment="todAdjustment"
                      :todSource="todSource"
                      :discountProduct="getProductDiscount"
                      :discountBookings="docketDiscount"
                      :booking-discount="docketDiscount"
                      :discCodeBookings="discountCodeData?.discountBookings || 0"
                      :discCodeProduct="discountCodeData?.discountProduct || 0"
                      :discMemBookings="userMembershipDiscounts?.discountFurtherBookings || props?.membership?.discountFurtherBookings || 0"
                      :discMemProducts="userMembershipDiscounts?.discountProduct || props?.membership?.discountProduct || 0"
                      :discountEvents="userMembershipDiscounts?.discountEvents || props?.membership?.discountEvents || 0"
                      :discountSources="discountSources"
                      :discountApplied="discountApplied"
                      :owner="form?.owner"
                      :bookingProp="props?.booking"
                      :bookingAmount="bookingTotal"
                      :bookingSlot="bookingSlot"
                      :payMethods="props?.booking?.payMethod"
                      type="Booking"
                      source="Booking"
                      :dpid="props?.dpid"
                      :canPayInStore="canPayInStore()"
                      :afterHours="afterHours"
                      @paymentTabChange="(tab) => selectedPaymentTab = tab"
                      @closeDialogForPayLater="closeDialogForPayLater"
                      @paymentSuccess="handlePaymentSuccess"
                      @goToPosPayment="gotToPos"
                      @updateMessage="
                        handleUpdateErrorMessage(
                          $event?.isVisible,
                          $event?.message,
                          $event?.color,
                          $event?.icon
                        )
                      "
                      @isConfirmed="clearInactivityTimer()"
                      @refetch="async () => { await refetchBooking(); await fetchPaymentStepData(); }"
                      @addNewCardDialog="emit('addNewCardDialog', true)"
                      @closeDialog="emit('closeDialog', false)"
                      @closeBooking="closeDialog"
                      @bookingChange="handleGetBooking"
                    />
                  </div>
                </VWindowItem>
              </VForm>
            </VWindow>
          </VCol>
        </VRow>
      </VCardText>

      <!-- Stepper Edit Content Ends -->
      <div>
        <div class="d-flex flex-column gap-2 pb-4 bold_tag">
          <div class="pl-5" v-if="isInCurrentTimeSlot">
            Some Details can only be changed by staff as booking in current time slot
          </div>
          <!-- <div class="pl-5" v-if="isOutHourWarningVisible">
            Some details can only be changed by staff as booked for out of hours
          </div> -->
        </div>
        <div v-if="finishedLoad">
          <VAlert
            :color="globalErrorMessage?.isVisible ? (globalErrorMessage?.color || 'error') : globalInfoMessage.color"
          >
            <div class="bold_tag d-md-flex justify-space-between gap-6">
              <div>
                <v-icon :icon="globalErrorMessage?.isVisible ? (globalErrorMessage?.icon || 'tabler-xbox-x') : (globalInfoMessage?.icon || 'mdi-info')" class="me-3"></v-icon>
                <template v-if="globalErrorMessage?.isVisible">
                  {{ globalErrorMessage?.message }}
                </template>
                <template v-else>
                  <span v-if="globalInfoMessage?.due">${{globalInfoMessage?.due}}</span>
                  <span v-if="globalInfoMessage?.todAdjustment">
                    <v-tooltip activator="parent">
                      Time Of Day Pricing {{ globalInfoMessage.todAdjustment > 0 ? '+' : '' }}{{ globalInfoMessage.todAdjustment }}%
                    </v-tooltip>
                    <v-icon icon="mdi-clock-outline" size="x-small"></v-icon>
                  </span>
                  {{globalInfoMessage?.message.split(")")[0]}}
                  <span>
                    <v-tooltip activator="parent">
                      The amount of Coupons required to fully cover the<br/> booked time (extras can't be paid with coupons)
                    </v-tooltip>
                    <v-icon v-if="globalInfoMessage?.message.indexOf(')') != -1" icon="mdi-info"></v-icon>
                  </span>
                  <span v-if="globalInfoMessage?.message.indexOf(')') != -1">)
                    {{ globalInfoMessage?.message.split(")")[1] }}</span>
                </template>
              </div>
              <div class="d-flex flex-wrap gap-4 align-left" v-if="uniqueDocketNumber().length">
                Dockets
                <a
                  v-for="d in uniqueDocketNumber()" :key="d.id"
                  @click.prevent="openReceipt(d.expand.docket.id)"
                  class="docket-link"
                >
                  #{{d.expand.docket.docketNumber}}
                </a>
              </div>
            </div>
          </VAlert>
          <div
            v-if="canOrderFood && currentLocationDetails?.webshopEnabled && props?.booking?.id && (props?.booking?.state == 'Confirmed' || props.booking?.state === 'Pending')"
            class="pa-3 d-flex justify-end bg-background"
          >
            <VBtn color="primary" prepend-icon="mdi-food" @click.prevent="gotoFood()">Add Food to booking</VBtn>
          </div>
        </div>
        <div v-else>
          <VAlert><v-progress-circular indeterminate class="me-3"></v-progress-circular> Loading...</VAlert>
        </div>
      </div>
      <StepperFooter v-if="props?.booking?.state != 'Deleted'"
        :booking="props?.booking"
        :isBookingViewOnly="isBookingViewOnly"
        :currentStep="currentStep"
        :bookingSteps="bookingSteps"
        :isEditBooking="isEditBooking"
        :bookingStatus="bookingStatus"
        :isYetToPayDues="isYetToPayDues"
        :isDirty="isBookingDirty"
        :canEditBooking="canEditBooking()"
        :haveEditAccess="haveEditAccess"
        :haveCreateAccess="haveCreateAccess"
        :role="role"
        :canPayInStore="canPayInStore()"
        :isPayInStoreSelected="selectedPaymentTab === 'payInStore'"
        :recurringDetails="props.recurringDetails"
        :activityList="props.activityList"
        :userId="props.userId"
        @closeDialog="closeDialog"
        @closeDialogForPayLater="closeDialogForPayLater"
        @handleSubmit="handleSubmit"
        @handlePaymentDues="handlePaymentDues"
        @editBooking="isBookingViewOnly = false; editingBooking()"
        @prevStep="currentStep--"
        @goToNextStep="goToNextStep"
        @refetchBookingData="emit('refetchBookingData', props?.booking?.id)"
      />
      <div v-else>
        <v-alert color="error" variant="tonal" class="rounded-0">
          <div class="d-flex">
            <h4>
              This booking has been deleted
              <VTooltip activator="parent" location="bottom">
                <div>{{ props?.booking?.deletedReason }}</div>
                <span v-if="props?.booking?.deletedEmailSent">
                  Deleted email sent on {{ moment(props?.booking?.deletedEmailSent).format('DD-MM-YYYY hh:mma') }}
                </span>
                <template v-slot:activator="{ props }">
                  <v-icon icon="mdi-information-outline"/>
                </template>
              </VTooltip>
            </h4>
          </div>
        </v-alert>
        <VCardText class="d-flex align-end flex-column">
          <VBtn
            variant="flat"
            color="secondary"
            @click="closeDialog"
          >
            Close
          </VBtn>
        </VCardText>
      </div>
    </VCard>

    <!-- Booking cancel confirmation dialog -->
    <BookingCancelConfirmDialog
      :is-open="isCancelDialogVisible"
      :activity="
        activityList?.find(
          (activity: any) => activity.id === props?.booking?.activity,
        )
      "
      :is-admin="(role === 'Admin' || haveEditAccess || haveCreateAccess) && !isUserMode"
      @closeDialog="isCancelDialogVisible = $event"
      @cancelBooking="confirmCloseBooking()"
      @leavePending="isCancelDialogVisible = false; emit('closeDialog', false)"
    />

    <!-- Error date list dialog -->
    <ErrorWarnDatesDialog
      :is-open="isBookingWarningDialogVisible"
      :dates="errorDateList"
      @closeDialog="isBookingWarningDialogVisible = false"
    />
  </VDialog>

  <DialogReceipt :docketId="docketinReceipt" v-model:showDialog="showReceiptDialog" />
</template>

<style scoped lang="scss">
.v-slide-group__content {
  padding: 0.625rem;
}

.stepper-icon-step {
  margin-block-start: 0.625rem;
}

.stepper-icon {
  border-radius: 0.3125rem;
  background-color: rgb(var(--v-theme-primary));
  color: rgba(var(--v-theme-on-primary));
}

.stepper-form-header {
  padding: 1.25rem;
  font-size: 0.625rem;
}

.member_list_selection {
  .v-field__input {
    max-block-size: 7.5rem;
    overflow-y: auto;
    scrollbar-width: thin;
  }
}

.docket-link {
  color: white;

  &:hover {
    color: rgb(var(--v-theme-primary));
  }
}
</style>
